import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Modal from 'flarum/common/components/Modal';
import ComposerBody from 'flarum/forum/components/ComposerBody';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ConfirmDocumentUnload from 'flarum/common/components/ConfirmDocumentUnload';
import avatar from 'flarum/common/helpers/avatar';
import listItems from 'flarum/common/helpers/listItems';
import ItemList from 'flarum/common/utils/ItemList';
import classList from 'flarum/common/utils/classList';
import icon from 'flarum/common/helpers/icon';
import LogInModal from 'flarum/forum/components/LogInModal';

const TYPE_KEYS = ['sell', 'buy', 'swap', 'group'];
const TYPE_LABELS = { sell: '出售', buy: '求购', swap: '交换', group: '组局' };

/** 中羽等级多选：1～9，步长 0.5 */
const GROUP_SKILL_STEP_VALUES = (() => {
  const a = [];
  for (let k = 2; k <= 18; k++) a.push(k / 2);
  return a;
})();

const GROUP_PLAY_TYPES = [
  { id: 'singles', label: '单打' },
  { id: 'ms', label: '男单' },
  { id: 'ws', label: '女单' },
  { id: 'doubles', label: '双打' },
  { id: 'md', label: '男双' },
  { id: 'wd', label: '女双' },
  { id: 'xd', label: '混双' },
  { id: 'any', label: '不限' },
];

function groupPlayTypeLabel(id) {
  return GROUP_PLAY_TYPES.find((p) => p.id === id)?.label || id || '—';
}

/** 登录用户可见交易备注；游客仅依赖后端清空 content，此处双保险 */
function canViewBadmintonTradeRemark() {
  return !!(app.session && app.session.user);
}

const TIER_LABELS = { student: '学生', staff: '教职工', alumni: '校友' };

/** 与后端 TradePricing::calendarPricingReason 一致，用于说明计价规则 */
const CALENDAR_SUMMARY_LABELS = {
  weekday_slot: '工作日按时段 A/B/C 档位对应「学生基础价」；教职工、校友再乘后台倍数。',
  weekend_c: '双休日本单各时段均按 C 档学生基础价累计（再乘身份倍数）。',
  holiday_c: '节假日本单各时段均按 C 档学生基础价累计（再乘身份倍数）。',
  special_date: '本日为后台「特殊日期档位」覆盖日，已按该日规则计价。',
};

function formatSwapSlotCourtLabel(config, venueId, piece) {
  const cid = piece.court_id;
  const ep = piece.court_edge_pref;
  if (ep === 'edge') return '边场';
  if (ep === 'non_edge') return '非边场';
  if (cid != null && Number(cid) > 0) return courtNameById(config, cid) || `场地 #${cid}`;
  return '不限';
}

/** 仅发布表单：边场选项后括号列出后台标记为边场的场地名 */
function edgeCourtOptionLabelForPublish(config, venueId) {
  if (!config?.courts || venueId == null || venueId === '') return '边场';
  const edges = config.courts.filter(
    (c) => Number(c.venue_id) === Number(venueId) && Number(c.is_edge) !== 0 && c.is_enabled !== 0
  );
  if (edges.length === 0) return '边场';
  const names = edges.map((c) => c.name || String(c.id)).join('、');
  return `边场（${names}）`;
}

function parseSwapSlotMapSelection(raw) {
  if (raw === '__edge__') return { court_id: null, court_edge_pref: 'edge' };
  if (raw === '__non_edge__') return { court_id: null, court_edge_pref: 'non_edge' };
  if (raw === undefined || raw === null || raw === '') return { court_id: null, court_edge_pref: null };
  const n = parseInt(String(raw), 10);
  if (Number.isFinite(n) && n > 0) return { court_id: n, court_edge_pref: null };
  return { court_id: null, court_edge_pref: null };
}

/** 无 config 时详情弹窗等处：边场/非边场仅作展示标签，不展开具体编号 */
function swapPayloadSlotCaption(r) {
  if (!r || typeof r !== 'object') return '';
  if (r.court_edge_pref === 'edge') return '边场';
  if (r.court_edge_pref === 'non_edge') return '非边场';
  if (r.court_label) return r.court_label;
  if (r.court_id) return r.court_name || `场地 #${r.court_id}`;
  return '不限';
}

function courtDisplayForTrade(t, config = null) {
  const swapFirst = t.trade_type === 'swap' && t.payload?.swap_have?.slots?.[0] ? t.payload.swap_have.slots[0] : null;
  if (swapFirst) {
    if (swapFirst.court_edge_pref === 'edge') return '边场';
    if (swapFirst.court_edge_pref === 'non_edge') return '非边场';
    if (swapFirst.court_label) return swapFirst.court_label;
    if (swapFirst.court_id) return swapFirst.court_name || t.court_name || `场地 #${swapFirst.court_id}`;
    return '不限';
  }
  const pref = t.payload && t.payload.court_edge_pref;
  if (pref === 'edge') return '边场';
  if (pref === 'non_edge') return '非边场';
  return t.court_name || '未指定';
}

/** 列表展示：已有多少人查看过联系方式（意向次数） */
function tradeViewCountLine(t) {
  const n = t.intent_count != null ? Number(t.intent_count) : 0;
  return `已有 ${Number.isFinite(n) ? n : 0} 人查看该信息`;
}

/** 禁止 Flarum 对扩展 JSON 错误弹出空的站内 Alert，改由本页 toast 展示 */
function silentFlarumRequestError() {}

/** 部分反代/环境可能对 4xx 仍返回 HTTP 200，此时需看 body.ok */
function isBadmintonFailureResponse(resp) {
  return resp && typeof resp === 'object' && resp.ok === false;
}

/** 将扩展 API 返回的 JSON 转成人话（含 error 码与附带字段） */
function humanMessageFromBadmintonBody(body) {
  if (!body || typeof body !== 'object') return '';
  const code = body.error;
  if (!code || typeof code !== 'string') return '';

  switch (code) {
    case 'trade_not_found':
      return '未找到该条信息，可能已被删除。';
    case 'not_trade_owner':
      return '你不是该信息的发布者，无法撤下。';
    case 'trade_not_withdrawable': {
      const st = body.status ? String(body.status) : '';
      const statusTip = {
        removed: '该信息已撤下，无法再次撤下。',
        completed: '该信息已完成，无法撤下。',
        invalid: '该信息已失效，无法撤下。',
      };
      return statusTip[st] || `该信息当前状态为「${tradeStatusLabel(st)}」，无法撤下。`;
    }
    case 'withdraw_failed':
      return '撤下失败（数据库未更新），请刷新页面后重试。';
    case 'invalid_id':
      return '无效的交易编号（链接中的 ID）。请刷新页面后重试；若持续出现请联系管理员。';
    case 'trade_restricted':
      return '你的账号暂不可发布场地交易。';
    case 'missing_required_fields':
      return '缺少必填项：交易类型或日期。';
    case 'invalid_trade_type':
      return '交易类型无效。';
    case 'invalid_trade_date':
      return '日期格式应为 YYYY-MM-DD。';
    case 'wechat_required':
      return '请填写微信号。';
    case 'invalid_payload':
      return '请求数据格式错误（payload）。';
    case 'slots_count_invalid':
      return '出售/求购时请至少选择一个时段。';
    case 'invalid_slot_entry':
      return '时段数据格式错误。';
    case 'duplicate_slots':
      return '请勿重复选择相同的场馆/时段/场地组合。';
    case 'slot_closed_for_trading':
      return '该日期或时段已关闭交易，无法发布。';
    case 'sell_slots_over_limit': {
      const lim = body.limit != null ? `（本场馆允许同一单最多 ${body.limit} 个时段）` : '';
      return `出售时段数量超过限制${lim}`;
    }
    case 'active_publish_limit': {
      const cur = body.current != null ? `当前进行中 ${body.current}` : '';
      const lim = body.limit != null ? `上限 ${body.limit}` : '';
      return `同时进行中的发布数已达上限${lim ? `（${lim}${cur ? `，${cur}` : ''}）` : ''}，请先撤下部分信息后再发布。`;
    }
    case 'price_above_standard': {
      const mx = body.max_price != null ? `允许的最高合计价为 ¥${body.max_price}` : '';
      const tier = body.tier ? TIER_LABELS[body.tier] || body.tier : '';
      const idTip = tier ? `（当前计价身份：${tier}）` : '';
      return `价格高于标准价合计上限（出售/求购均不可超过）。${mx ? ` ${mx}。` : ''}${idTip}`;
    }
    case 'price_required':
      return '出售/求购须填写价格。';
    case 'price_invalid':
      return '价格须为大于 0 的有效数字。';
    case 'respond_limit': {
      const lim = body.limit != null ? String(body.limit) : '2';
      return `每人最多同时响应 ${lim} 条进行中的信息，请先等部分信息发布方撤下或失效后再查看。`;
    }
    case 'trade_not_open':
      return '该信息已不是进行中状态，无法查看联系方式。';
    case 'cannot_intent_own_trade':
      return '不能响应自己发布的信息。';
    case 'intent_duplicate':
      return '你已响应该信息，可直接查看联系方式。';
    case 'intent_full':
      return '该信息发布方设置的查看人数已满。';
    case 'intent_failed':
      return '记录响应失败，请稍后重试。';
    case 'invalid_court_edge_pref':
      return '场地偏好参数无效（仅支持 edge / non_edge）。';
    case 'court_edge_pref_conflict':
      return '不能同时选择「边场/非边场」与具体场地编号，请只选其一。';
    case 'court_edge_pref_not_allowed':
      return '当前场馆未在后台开启此项，无法使用「边场/非边场」展示说明。';
    case 'court_edge_pref_not_applicable':
      return '交换发布不能在请求体顶层携带边场说明，请仅在每段时段内选择。';
    case 'swap_court_required':
      return '交换场地信息校验失败（若持续出现请刷新页面后重试）。';
    case 'swap_slots_not_consecutive':
      return '同一侧若选择两个时段，须在时间上相邻（按场馆配置的时段顺序连续）。';
    case 'swap_payload_invalid':
      return '交换数据不完整，请重新选择「拥有」与「希望换得」的场次。';
    case 'swap_date_mismatch':
      return '交换数据不一致（拥有日期与提交日期不符），请刷新后重试。';
    case 'swap_venue_required':
      return '交换须选择双方场馆。';
    case 'swap_have_slots_count':
      return '「我拥有的」时段须为 1～2 个。';
    case 'swap_want_slots_count':
      return '「希望换得」的时段数须为 1～拥有时段数，且不能超过后者。';
    case 'group_venue_slot_required':
      return '组局请选择场馆与时段。';
    case 'group_missing_invalid':
      return '缺男生、缺女生人数须分别为 0～99，且两者之和至少为 1。';
    case 'group_skill_levels_invalid':
      return '请至少选择一个有效的中羽级别（1～9，可半级）。';
    case 'group_play_type_invalid':
      return '请选择有效的组局类型。';
    case 'subscription_active_limit': {
      const lim = body.limit != null ? String(body.limit) : '2';
      return `同时进行中的订阅最多 ${lim} 条，请先取消一条后再试。`;
    }
    case 'subscription_duplicate':
      return '你已有一条相同的进行中订阅。';
    case 'subscription_not_found':
      return '未找到该订阅。';
    case 'not_subscription_owner':
      return '这不是你的订阅，无法操作。';
    case 'subscription_not_withdrawable': {
      const st = body.status ? String(body.status) : '';
      if (st === 'removed') return '该订阅已结束。';
      return `该订阅当前不可取消（状态：${subscriptionStatusLabel(st)}）。`;
    }
    case 'subscriptions_unavailable':
      return '订阅功能未就绪，请联系管理员。';
    case 'venue_slot_required':
      return '请选择有效的场馆与时段。';
    case 'venue_slot_mismatch':
      return '所选时段不属于当前场馆，请刷新页面后重试。';
    case 'slot_not_found':
      return '时段不存在或已被删除。';
    default:
      return `错误代码：${code}`;
  }
}

/** 解析 Flarum request 异常（支持扩展自定义 JSON 与 Flarum errors[]） */
function parseBadmintonRequestError(e) {
  const tryBody = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    if (typeof obj.error === 'string' && obj.error !== '') {
      const fromExt = humanMessageFromBadmintonBody(obj);
      if (fromExt) return fromExt;
    }
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
      const first = obj.errors[0];
      const detail = first && (first.detail || first.title);
      if (detail) return String(detail);
    }
    return humanMessageFromBadmintonBody(obj);
  };

  let msg = '';
  if (e && typeof e.response === 'object' && e.response !== null) {
    msg = tryBody(e.response);
  }
  if (!msg && e && typeof e.responseText === 'string' && e.responseText) {
    try {
      msg = tryBody(JSON.parse(e.responseText));
    } catch (_) {
      const t = e.responseText.trim();
      if (t.length > 0 && t.length < 600) msg = t;
    }
  }
  if (!msg && e?.xhr && typeof e.xhr.responseText === 'string' && e.xhr.responseText) {
    try {
      msg = tryBody(JSON.parse(e.xhr.responseText));
    } catch (_) {
      /* ignore */
    }
  }
  if (!msg) {
    const code = e?.status ?? e?.xhr?.status;
    if (code) return `请求失败（HTTP ${code}）。`;
    return '请求失败，请稍后重试。';
  }
  return msg;
}

function publishFieldLabel(required, text, hint) {
  return (
    <span className="badminton-field-label">
      <span className="badminton-field-label-title">
        {required ? (
          <abbr className="badminton-req" title="必填">
            *
          </abbr>
        ) : null}
        {text}
      </span>
      {hint ? <span className="badminton-field-hint">{hint}</span> : null}
    </span>
  );
}

/**
 * 底部 App-composer 自定义面板（无帖子 TextEditor，沿用 ComposerBody 布局与 Composer 镀铬条）
 */
class BadmintonComposerPanel extends ComposerBody {
  static initAttrs(attrs) {
    super.initAttrs(attrs);
    attrs.user = attrs.user || app.session.user;
    attrs.className = classList('BadmintonComposerPanel', attrs.className);
  }

  hasChanges() {
    return false;
  }

  view() {
    return (
      <ConfirmDocumentUnload when={() => false}>
        <div className={classList('ComposerBody', this.attrs.className)}>
          {avatar(this.attrs.user, { className: 'ComposerBody-avatar' })}
          <div className="ComposerBody-content">
            <ul className="ComposerBody-header">{listItems(this.headerItems().toArray())}</ul>
            <div className="ComposerBody-editor">
              <div className="TextEditor">
                <div
                  className="TextEditor-editorContainer FormControl Composer-flexible badminton-composer-flex"
                  style="overflow-x: hidden;"
                >
                  {this.panelContent()}
                </div>
                {this.panelFooter()}
              </div>
            </div>
          </div>
          <LoadingIndicator display="unset" containerClassName={classList('ComposerBody-loading', this.loading && 'active')} size="large" />
        </div>
      </ConfirmDocumentUnload>
    );
  }

  panelContent() {
    return null;
  }

  panelFooter() {
    return null;
  }

  onsubmit() {}
}

class BadmintonWithdrawConfirmComposer extends BadmintonComposerPanel {
  static initAttrs(attrs) {
    super.initAttrs(attrs);
    attrs.className = classList('BadmintonWithdrawConfirmComposer', attrs.className);
  }

  headerItems() {
    const items = super.headerItems();
    items.add('title', <h3>确认撤下</h3>, 100);
    return items;
  }

  panelContent() {
    const t = this.attrs.trade || {};
    return (
      <div className="Form Form--centered" style="padding: 8px 0;">
        <p style="margin:0 0 8px;">确认要撤下这条信息吗？</p>
        <p style="margin:0 0 6px; font-size: 13px; color: var(--muted-color);">日期：{t.trade_date || '未知'}</p>
        <p style="margin:0 0 6px; font-size: 13px; color: var(--muted-color);">场馆：{t.venue_name || '未指定'}</p>
        <p style="margin:0; font-size: 13px; color: var(--muted-color);">
          时段：{t.payload?.merged_slots_text || t.slot_name || '未指定'}
        </p>
      </div>
    );
  }

  onremove(vnode) {
    if (typeof this.attrs.onhide === 'function') this.attrs.onhide();
    super.onremove(vnode);
  }

  panelFooter() {
    return (
      <ul className="TextEditor-controls Composer-footer">
        <li>
          <Button className="Button" onclick={() => app.composer.close()}>
            取消
          </Button>
        </li>
        <li>
          <Button
            className="Button Button--primary"
            onclick={() => {
              if (typeof this.attrs.onconfirm === 'function') this.attrs.onconfirm();
              app.composer.close();
            }}
          >
            确认撤下
          </Button>
        </li>
      </ul>
    );
  }
}

class SubscribeSlotComposer extends BadmintonComposerPanel {
  static initAttrs(attrs) {
    super.initAttrs(attrs);
    attrs.className = classList('BadmintonSubscribeComposer', attrs.className);
  }

  oninit(vnode) {
    super.oninit(vnode);
    this.draft = {
      trade_type: vnode.attrs.defaultType || 'sell',
      court_id: '',
      court_edge_pref: '',
    };
    this.submitting = false;
  }

  headerItems() {
    const items = super.headerItems();
    items.add('title', <h3>订阅场地动态</h3>, 100);
    return items;
  }

  courtSelectValue() {
    if (this.draft.court_edge_pref === 'edge') return '__edge__';
    if (this.draft.court_edge_pref === 'non_edge') return '__non_edge__';
    return this.draft.court_id === '' ? '' : String(this.draft.court_id);
  }

  onCourtChange(e) {
    const v = e.target.value;
    if (v === '__edge__') {
      this.draft.court_edge_pref = 'edge';
      this.draft.court_id = '';
    } else if (v === '__non_edge__') {
      this.draft.court_edge_pref = 'non_edge';
      this.draft.court_id = '';
    } else {
      this.draft.court_edge_pref = '';
      this.draft.court_id = v;
    }
  }

  venueAllowsEdge(board) {
    const v = board.config?.venues?.find((x) => Number(x.id) === Number(board.selectedVenueId));
    return v && Number(v.allow_edge_filter) !== 0;
  }

  activeSubCount(board) {
    return (board.mySubscriptions || []).filter((s) => s.status === 'open').length;
  }

  panelContent() {
    const board = this.attrs.board;
    const slot = this.attrs.slot;
    const vCur = board.currentVenue();
    const slotLabel = slot ? slot.name || `${slot.start_time}-${slot.end_time}` : '';
    const allows = this.venueAllowsEdge(board);
    const rows = board.courtsForVenue(board.selectedVenueId);
    const nAct = this.activeSubCount(board);

    return (
      <div className="Form Form--centered" style="padding: 8px 0;">
        <div className="Form-group">
          <p style="margin:0; font-size: 13px; color: var(--muted-color); line-height: 1.5;">
            当其他用户新发布的<strong>「{TYPE_LABELS[this.draft.trade_type]}」</strong>
            满足本页所选日期、场馆、时段及下方场地条件时，你将收到<strong>站内通知</strong>。
          </p>
        </div>
        <div className="Form-group">
          <p className="badminton-subscribe-modal-meta" style="margin:0;">
            <strong>日期</strong> {board.selectedDate} ·<strong> 场馆</strong> {vCur?.name || '—'} ·<strong> 时段</strong> {slotLabel}
          </p>
        </div>
        <div className="Form-group">
          <label className="badminton-form-field">
            {publishFieldLabel(true, '订阅类型', '')}
            <select
              className="FormControl"
              value={this.draft.trade_type}
              onchange={(e) => {
                this.draft.trade_type = e.target.value;
                m.redraw();
              }}
            >
              {TYPE_KEYS.map((k) => (
                <option key={k} value={k}>
                  {TYPE_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="Form-group">
          <label className="badminton-form-field">
            {publishFieldLabel(false, '场地条件', '不选编号即与发布页的「不限」相同')}
            <select
              className="FormControl"
              value={this.courtSelectValue()}
              onchange={(e) => {
                this.onCourtChange(e);
                m.redraw();
              }}
            >
              <option value="">不限 / 不指定</option>
              {allows ? (
                <optgroup label="对他人展示为（不计入具体场地号）">
                  <option value="__edge__">{edgeCourtOptionLabelForPublish(board.config, board.selectedVenueId)}</option>
                  <option value="__non_edge__">非边场</option>
                </optgroup>
              ) : null}
              {rows.length ? (
                <optgroup label="具体场地">
                  {rows.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </label>
        </div>
        {nAct >= 2 ? (
          <p className="badminton-subscribe-limit-warn" style="margin:0;">
            你已有 2 条进行中的订阅，请先取消一条后再添加。
          </p>
        ) : (
          <p style="margin:0; font-size: 12px; color: var(--muted-color);">
            进行中订阅：{nAct} / 2
          </p>
        )}
      </div>
    );
  }

  panelFooter() {
    const board = this.attrs.board;
    const nAct = this.activeSubCount(board);
    return (
      <ul className="TextEditor-controls Composer-footer">
        <li>
          <Button className="Button" disabled={this.submitting} onclick={() => app.composer.close()}>
            取消
          </Button>
        </li>
        <li>
          <Button
            className="Button Button--primary"
            loading={this.submitting}
            disabled={this.submitting || nAct >= 2}
            onclick={() => this.submit()}
          >
            保存订阅
          </Button>
        </li>
      </ul>
    );
  }

  async submit() {
    const board = this.attrs.board;
    const slot = this.attrs.slot;
    if (this.activeSubCount(board) >= 2) return;
    this.submitting = true;
    m.redraw();
    const api = app.forum.attribute('apiUrl');
    const body = {
      trade_type: this.draft.trade_type,
      trade_date: board.selectedDate,
      venue_id: Number(board.selectedVenueId),
      slot_id: Number(slot?.id || 0),
    };
    if (this.draft.court_edge_pref) {
      body.court_edge_pref = this.draft.court_edge_pref;
    } else if (this.draft.court_id) {
      body.court_id = Number(this.draft.court_id);
    }
    try {
      const resp = await app.request({
        method: 'POST',
        url: `${api}/badminton/me/subscriptions`,
        body,
        errorHandler: silentFlarumRequestError,
      });
      if (isBadmintonFailureResponse(resp)) {
        board.pushBoardError(humanMessageFromBadmintonBody(resp) || '订阅失败');
        this.submitting = false;
        m.redraw();
        return;
      }
      board.toastOk = '已保存订阅。有新发布时会通过站内通知提醒你。';
      await board.loadMySubscriptions();
      this.submitting = false;
      app.composer.close();
      m.redraw();
    } catch (e) {
      board.pushBoardError(parseBadmintonRequestError(e));
      this.submitting = false;
      m.redraw();
    }
  }
}

class ViewTradeConfirmComposer extends BadmintonComposerPanel {
  static initAttrs(attrs) {
    super.initAttrs(attrs);
    attrs.className = classList('BadmintonViewTradeConfirmComposer', attrs.className);
  }

  headerItems() {
    const items = super.headerItems();
    items.add('title', <h3>确认查看</h3>, 100);
    return items;
  }

  onremove(vnode) {
    if (typeof this.attrs.onhide === 'function') this.attrs.onhide();
    super.onremove(vnode);
  }

  panelContent() {
    return (
      <div className="Form Form--centered" style="padding: 8px 0;">
        <p style="margin:0;">确认要查看吗？</p>
      </div>
    );
  }

  panelFooter() {
    return (
      <ul className="TextEditor-controls Composer-footer">
        <li>
          <Button className="Button" onclick={() => app.composer.close()}>
            取消
          </Button>
        </li>
        <li>
          <Button
            className="Button Button--primary"
            onclick={() => {
              if (typeof this.attrs.onconfirm === 'function') this.attrs.onconfirm();
              app.composer.close();
            }}
          >
            确认
          </Button>
        </li>
      </ul>
    );
  }
}

class TradeDetailComposer extends BadmintonComposerPanel {
  static initAttrs(attrs) {
    super.initAttrs(attrs);
    attrs.className = classList('BadmintonTradeDetailComposer', attrs.className);
  }

  headerItems() {
    const items = super.headerItems();
    items.add('title', <h3>场地与联系</h3>, 100);
    return items;
  }

  panelContent() {
    const t = this.attrs.trade || {};
    const wx = this.attrs.publisherWechat || '';
    const cfg = this.attrs.config || null;
    return (
      <div className="badminton-trade-detail" style="padding: 8px 0;">
        <p>
          <strong>类型</strong> {TYPE_LABELS[t.trade_type] || t.trade_type}
        </p>
        <p>
          <strong>日期</strong> {t.trade_date || '—'}
        </p>
        <p>
          <strong>场馆</strong> {t.venue_name || '—'}
        </p>
        <p>
          <strong>时段</strong>{' '}
          {t.trade_type === 'swap' && t.payload?.swap_have && t.payload?.swap_want ? (
            <span>
              换出：{t.payload.swap_have.trade_date || t.trade_date} ·{' '}
              {Array.isArray(t.payload.swap_have.slots)
                ? t.payload.swap_have.slots
                    .map((r) => {
                      const st = r.slot_text || `#${r.slot_id}`;
                      const cn = cfg ? formatSwapSlotCourtLabel(cfg, r.venue_id ?? t.venue_id, r) : swapPayloadSlotCaption(r);
                      return `${st}（${cn}）`;
                    })
                    .join('、')
                : '—'}
              <br />
              换入：{t.payload.swap_want.trade_date || '—'} ·{' '}
              {Array.isArray(t.payload.swap_want.slots)
                ? t.payload.swap_want.slots
                    .map((r) => {
                      const st = r.slot_text || `#${r.slot_id}`;
                      const cn = cfg ? formatSwapSlotCourtLabel(cfg, r.venue_id ?? t.venue_id, r) : swapPayloadSlotCaption(r);
                      return `${st}（${cn}）`;
                    })
                    .join('、')
                : '—'}
            </span>
          ) : (
            <span>{t.payload?.merged_slots_text || t.slot_name || '—'}</span>
          )}
        </p>
        {t.trade_type === 'group' && t.payload ? (
          <p>
            <strong>组局信息</strong>
            <br />
            已订场：{t.payload.group_has_court ? '是' : '否'}
            <br />
            缺人：
            {t.payload.group_missing_male != null || t.payload.group_missing_female != null
              ? `男生 ${t.payload.group_missing_male ?? 0} 人、女生 ${t.payload.group_missing_female ?? 0} 人`
              : t.payload.group_missing_count != null
                ? `共 ${t.payload.group_missing_count} 人（旧格式）`
                : '—'}
            <br />
            类型：{groupPlayTypeLabel(t.payload.group_play_type)}
            <br />
            希望级别：
            {Array.isArray(t.payload.group_skill_levels)
              ? t.payload.group_skill_levels
                  .map((n) => (Math.abs(Number(n) % 1) < 1e-6 ? String(Math.round(Number(n))) : String(n)))
                  .join('、')
              : '—'}
          </p>
        ) : null}
        <p>
          <strong>场地</strong> {courtDisplayForTrade(t, cfg)}
        </p>
        <p>
          <strong>价格</strong>{' '}
          {t.trade_type === 'swap' ? '—' : t.price != null && Number(t.price) > 0 ? `¥${t.price}` : '—'}
        </p>
        {canViewBadmintonTradeRemark() && t.content ? (
          <p>
            <strong>备注</strong> {t.content}
          </p>
        ) : null}
        <p>
          <strong>发布方微信号</strong> {wx || '—'}
        </p>
      </div>
    );
  }

  panelFooter() {
    return (
      <ul className="TextEditor-controls Composer-footer">
        <li>
          <Button className="Button Button--primary" onclick={() => app.composer.close()}>
            关闭
          </Button>
        </li>
      </ul>
    );
  }
}

class SellNineHourConfirmModal extends Modal {
  className() {
    return 'BadmintonSellNineHourConfirmModal Modal--small';
  }

  title() {
    return this.attrs.tradeType === 'buy' ? '求购提醒' : '出售提醒';
  }

  onbeforeremove(vnode) {
    if (typeof this.attrs.onhide === 'function') this.attrs.onhide();
    return super.onbeforeremove(vnode);
  }

  content() {
    const isBuy = this.attrs.tradeType === 'buy';
    return (
      <div className="Modal-body">
        <div className="Form Form--centered">
          <div className="Form-group">
            <p style="margin:0; line-height: 1.5;">
              开场前9小时在「智慧场馆」退订场地，不收手续费，确认要{isBuy ? '发布求购' : '出售'}吗？
            </p>
          </div>
          <div className="Form-group badminton-modal-form-actions">
            <Button className="Button Button--block" type="button" onclick={() => this.hide()}>
              取消
            </Button>
            <Button
              className="Button Button--primary Button--block"
              type="button"
              onclick={() => {
                if (typeof this.attrs.onconfirm === 'function') this.attrs.onconfirm();
                this.hide();
              }}
            >
              {isBuy ? '确认求购' : '确认出售'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/** 快速发布（表单较长）使用大号 Modal，不与单例 Composer 争抢底栏 */
class BadmintonPublishTradeModal extends Modal {
  className() {
    return 'BadmintonPublishTradeModal Modal--large';
  }

  title() {
    const b = this.attrs.board;
    const ty = b?.publishDraft?.trade_type || 'sell';
    return `发布 · ${TYPE_LABELS[ty] || '场地'}`;
  }

  onbeforeremove(vnode) {
    if (this.attrs.board) this.attrs.board.showPublish = false;
    return super.onbeforeremove(vnode);
  }

  content() {
    return this.attrs.board.renderPublishFormContent({
      includeSubmitButton: true,
      outerClassName: 'Modal-body badminton-publish-modal-body badminton-publish-sheet',
    });
  }

  onsubmit(e) {
    e.preventDefault();
    this.attrs.board.submitPublish();
  }
}

class BadmintonAssistantModal extends Modal {
  className() {
    return 'BadmintonAssistantModal Modal--small';
  }

  title() {
    return '填写助手';
  }

  oninit(vnode) {
    super.oninit(vnode);
    this.text = '';
    this.inputType = 'text';
    this.imageDataUrl = '';
    this.loading = false;
    this.err = null;
    this.missing = [];
    this.warnings = [];
  }

  content() {
    const enabled = app.forum.attribute('acme-pku-badminton.assistant_enabled') === '1';
    if (!enabled) {
      return <div className="Modal-body">填写助手未开启（请联系管理员在后台开启）。</div>;
    }
    return (
      <>
        <div className="Modal-body">
          <p style="margin-top:0; color: var(--muted-color); line-height:1.5;">
            用自然语言描述你的需求，例如：<br />
            “4月2日 邱德拔 19:00-21:00 出售 价格 80 微信 xxx 备注：可分时段”
          </p>
          <div className="Form-group">
            <label className="badminton-form-field">
              <div style="font-size: 12px; color: var(--muted-color); margin-bottom: 6px;">输入方式</div>
              <select
                className="FormControl"
                value={this.inputType}
                onchange={(e) => {
                  this.inputType = e.target.value;
                  this.err = null;
                  m.redraw();
                }}
              >
                <option value="text">文字</option>
                <option value="asr">语音转文字（请使用悬浮助手）</option>
                <option value="image">图片 OCR</option>
              </select>
            </label>
          </div>
          {this.inputType === 'image' ? (
            <div className="Form-group">
              <label className="badminton-form-field">
                <div style="font-size: 12px; color: var(--muted-color); margin-bottom: 6px;">选择截图</div>
                <input
                  className="FormControl"
                  type="file"
                  accept="image/*"
                  onchange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      this.imageDataUrl = String(reader.result || '');
                      m.redraw();
                    };
                    reader.readAsDataURL(f);
                  }}
                />
                {this.imageDataUrl ? (
                  <div style="margin-top: 8px; font-size: 12px; color: var(--muted-color);">已选择图片（将用于 OCR）</div>
                ) : null}
              </label>
            </div>
          ) : null}
          <textarea
            className="FormControl"
            rows="5"
            value={this.text}
            oninput={(e) => {
              this.text = e.target.value;
            }}
            placeholder="请输入你的需求…"
          />
          {this.err ? <div style="margin-top:10px; color:#e74c3c;">{this.err}</div> : null}
          {this.missing && this.missing.length ? (
            <div style="margin-top:10px; color: var(--muted-color); font-size:12px;">
              仍需补充：{this.missing.join('、')}
            </div>
          ) : null}
          {this.warnings && this.warnings.length ? (
            <div style="margin-top:6px; color: var(--muted-color); font-size:12px;">
              提示：{this.warnings.join('；')}
            </div>
          ) : null}
        </div>
        <div className="Modal-footer">
          <Button className="Button Button--primary" type="submit" loading={this.loading} disabled={this.loading}>
            生成草稿
          </Button>
        </div>
      </>
    );
  }

  onsubmit(e) {
    e.preventDefault();
    this.submit();
  }

  async submit() {
    const board = this.attrs.board;
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }
    const text = String(this.text || '').trim();
    if (this.inputType === 'asr') {
      this.err = '语音识别请使用页面顶部悬浮「填写助手」入口。';
      m.redraw();
      return;
    }
    if (!text) {
      if (this.inputType !== 'image') {
        this.err = '请输入描述。';
        m.redraw();
        return;
      }
    }
    if (this.inputType === 'image' && !this.imageDataUrl) {
      this.err = '请先选择一张截图。';
      m.redraw();
      return;
    }
    this.loading = true;
    this.err = null;
    this.missing = [];
    this.warnings = [];
    m.redraw();

    try {
      const api = app.forum.attribute('apiUrl');
      const r = await app.request({
        method: 'POST',
        url: `${api}/badminton/assistant/draft`,
        body: {
          input_type: this.inputType,
          text,
          image: this.inputType === 'image' ? this.imageDataUrl : undefined,
          context: { selectedVenueId: board.selectedVenueId, selectedDate: board.selectedDate },
        },
      });
      this.missing = r?.missing || [];
      this.warnings = r?.warnings || [];
      board.applyAssistantDraft(r?.draft || {}, { missing: this.missing, warnings: this.warnings });
      this.loading = false;
      app.modal.close();
      m.redraw();
    } catch (e) {
      this.loading = false;
      this.err = '生成失败，请稍后重试。';
      m.redraw();
    }
  }
}

/**
 * 悬浮「填写助手」：顶部可拖动圆按钮，点击展开输入面板
 * - 复用现有 /badminton/assistant/draft 接口与 board.applyAssistantDraft
 * - 保持 human-in-the-loop：只回填草稿，不自动提交
 */
class BadmintonAssistantFloat extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.open = false;
    this.text = '';
    this.imageDataUrl = '';
    this.loading = false;
    this.err = null;
    this.missing = [];
    this.warnings = [];
    this.assistantMsgs = [];
    this.roundIndex = 0;
    this.maxRounds = Math.max(1, parseInt(String(app.forum.attribute('acme-pku-badminton.assistant_max_rounds') || '3'), 10) || 3);
    this.remainingRounds = this.maxRounds;
    this.latestDraft = {};
    this.recording = false;
    this.recordSecs = 0;
    this._recTimer = null;
    this._mediaStream = null;
    this._audioCtx = null;
    this._sourceNode = null;
    this._procNode = null;
    this._pcmChunks = [];

    this.dragging = false;
    this.dragMoved = false;
    this.dragStart = { x: 0, y: 0, px: 0, py: 0 };

    const saved = this.loadPos();
    this.pos = saved || { x: 16, y: 12 };
  }

  loadPos() {
    try {
      const raw = localStorage.getItem('acme_pku_badminton_assistant_float_pos');
      if (!raw) return null;
      const j = JSON.parse(raw);
      const x = Number(j?.x);
      const y = Number(j?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { x, y };
    } catch (e) {
      return null;
    }
  }

  savePos() {
    try {
      localStorage.setItem('acme_pku_badminton_assistant_float_pos', JSON.stringify(this.pos));
    } catch (e) {
      /* ignore */
    }
  }

  clampPos() {
    const btn = 58; // roughly button size incl. shadow
    const pad = 8;
    const w = window.innerWidth || 360;
    const h = window.innerHeight || 640;
    const xMax = Math.max(pad, w - btn - pad);
    const yMax = Math.max(pad, h - btn - pad);
    this.pos.x = Math.max(pad, Math.min(xMax, this.pos.x));
    this.pos.y = Math.max(pad, Math.min(yMax, this.pos.y));
  }

  onPointerDown(e) {
    if (!e) return;
    if (e.button != null && e.button !== 0) return;
    this.dragging = true;
    this.dragMoved = false;
    const cx = e.clientX != null ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const cy = e.clientY != null ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    this.dragStart = { x: cx, y: cy, px: this.pos.x, py: this.pos.y };
    try {
      if (e.target && e.target.setPointerCapture && e.pointerId != null) e.target.setPointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
  }

  onPointerMove(e) {
    if (!this.dragging) return;
    const cx = e.clientX != null ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const cy = e.clientY != null ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    const dx = cx - this.dragStart.x;
    const dy = cy - this.dragStart.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) this.dragMoved = true;
    this.pos.x = this.dragStart.px + dx;
    this.pos.y = this.dragStart.py + dy;
    this.clampPos();
    m.redraw();
  }

  onPointerUp() {
    if (!this.dragging) return;
    this.dragging = false;
    this.clampPos();
    this.savePos();
  }

  toggleOpen() {
    this.err = null;
    this.missing = [];
    this.warnings = [];
    if (!this.open) {
      this.maxRounds = Math.max(1, parseInt(String(app.forum.attribute('acme-pku-badminton.assistant_max_rounds') || '3'), 10) || 3);
      this.roundIndex = 0;
      this.remainingRounds = this.maxRounds;
      this.latestDraft = {};
      this.assistantMsgs = [];
    }
    this.open = !this.open;
  }

  stopRecordingInternal() {
    this.recording = false;
    if (this._recTimer) {
      clearInterval(this._recTimer);
      this._recTimer = null;
    }
    try {
      if (this._procNode) this._procNode.disconnect();
      if (this._sourceNode) this._sourceNode.disconnect();
    } catch (e) {
      /* ignore */
    }
    this._procNode = null;
    this._sourceNode = null;
    try {
      if (this._audioCtx) this._audioCtx.close();
    } catch (e) {
      /* ignore */
    }
    this._audioCtx = null;
    if (this._mediaStream) {
      try {
        this._mediaStream.getTracks().forEach((t) => t.stop());
      } catch (e) {
        /* ignore */
      }
    }
    this._mediaStream = null;
  }

  floatTo16BitPCM(float32) {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      let s = float32[i];
      if (s > 1) s = 1;
      if (s < -1) s = -1;
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }

  downsampleBuffer(buffer, inSampleRate, outSampleRate) {
    if (outSampleRate === inSampleRate) return buffer;
    if (outSampleRate > inSampleRate) return buffer;
    const ratio = inSampleRate / outSampleRate;
    const newLen = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLen);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
      let accum = 0;
      let count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }
      result[offsetResult] = count > 0 ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  encodeWavPcm16Mono(samplesInt16, sampleRate) {
    const bytesPerSample = 2;
    const blockAlign = bytesPerSample * 1;
    const byteRate = sampleRate * blockAlign;
    const dataSize = samplesInt16.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    const writeStr = (offset, str) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // bits
    writeStr(36, 'data');
    view.setUint32(40, dataSize, true);
    let o = 44;
    for (let i = 0; i < samplesInt16.length; i++, o += 2) view.setInt16(o, samplesInt16[i], true);
    return new Blob([buffer], { type: 'audio/wav' });
  }

  async startRecording() {
    if (this.recording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.err = '当前浏览器不支持录音。';
      m.redraw();
      return;
    }
    const asrEnabled = app.forum.attribute('acme-pku-badminton.assistant_asr_enabled') === '1';
    if (!asrEnabled) {
      this.err = '语音识别未开启（请联系管理员在后台开启）。';
      m.redraw();
      return;
    }
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }

    this.err = null;
    this.recordSecs = 0;
    this._pcmChunks = [];
    this.recording = true;
    m.redraw();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this._mediaStream = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      this._audioCtx = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      this._sourceNode = source;
      const proc = audioCtx.createScriptProcessor(4096, 1, 1);
      this._procNode = proc;
      source.connect(proc);
      proc.connect(audioCtx.destination);

      proc.onaudioprocess = (e) => {
        if (!this.recording) return;
        const input = e.inputBuffer.getChannelData(0);
        // 先下采样到 16k（讯飞推荐），再缓存
        const down = this.downsampleBuffer(input, audioCtx.sampleRate, 16000);
        this._pcmChunks.push(down);
      };

      this._recTimer = setInterval(() => {
        if (!this.recording) return;
        this.recordSecs += 1;
        if (this.recordSecs >= 60) {
          this.stopRecordingAndSend();
        } else {
          m.redraw();
        }
      }, 1000);
    } catch (e) {
      this.stopRecordingInternal();
      this.err = '无法开始录音（请检查浏览器权限）。';
      m.redraw();
    }
  }

  async stopRecordingAndSend() {
    if (!this.recording) return;
    // 停止采集
    this.stopRecordingInternal();

    // 合并 pcm
    const chunks = this._pcmChunks || [];
    this._pcmChunks = [];
    let total = 0;
    chunks.forEach((c) => (total += c.length));
    if (total <= 0) {
      this.err = '未录到有效语音。';
      m.redraw();
      return;
    }
    const merged = new Float32Array(total);
    let off = 0;
    chunks.forEach((c) => {
      merged.set(c, off);
      off += c.length;
    });
    const pcm16 = this.floatTo16BitPCM(merged);
    const wav = this.encodeWavPcm16Mono(pcm16, 16000);

    // 上传并自动生成草稿
    await this.submitAsrWav({ board: this._board, wavBlob: wav, durationSec: Math.min(60, this.recordSecs || 0) });
  }

  async submitAsrWav({ board, wavBlob, durationSec }) {
    const enabled = app.forum.attribute('acme-pku-badminton.assistant_enabled') === '1';
    if (!enabled) {
      this.err = '填写助手未开启（请联系管理员在后台开启）。';
      m.redraw();
      return;
    }
    this.loading = true;
    this.err = null;
    this.missing = [];
    this.warnings = [];
    m.redraw();
    try {
      const api = app.forum.attribute('apiUrl');
      const fd = new FormData();
      fd.append('audio', wavBlob, 'audio.wav');
      fd.append('duration_sec', String(durationSec || 0));
      fd.append('round_index', String(this.roundIndex + 1));
      fd.append('previous_draft_json', JSON.stringify(this.latestDraft || {}));
      const r = await app.request({
        method: 'POST',
        url: `${api}/badminton/assistant/asr-draft`,
        body: fd,
      });
      this.roundIndex = this.roundIndex + 1;
      this.missing = r?.missing || [];
      this.warnings = r?.warnings || [];
      this.remainingRounds = Math.max(0, Number(r?.remaining_rounds ?? this.maxRounds - this.roundIndex));
      this.latestDraft = r?.draft || this.latestDraft || {};
      if (r?.asr_text) this.assistantMsgs.push({ role: 'user', text: String(r.asr_text) });
      if (r?.assistant_reply) this.assistantMsgs.push({ role: 'assistant', text: String(r.assistant_reply) });
      if (this.assistantMsgs.length > 8) this.assistantMsgs = this.assistantMsgs.slice(-8);

      const shouldFill = !!r?.should_fill;
      if (shouldFill) {
        board.applyAssistantDraft(this.latestDraft || {}, { missing: this.missing, warnings: this.warnings });
      }
      this.loading = false;
      if (shouldFill) {
        this.open = false;
      }
      m.redraw();
    } catch (e) {
      this.loading = false;
      this.err = '语音识别/生成失败，请稍后重试。';
      m.redraw();
    }
  }

  async submitTextOrImage({ board }) {
    const enabled = app.forum.attribute('acme-pku-badminton.assistant_enabled') === '1';
    if (!enabled) {
      this.err = '填写助手未开启（请联系管理员在后台开启）。';
      m.redraw();
      return;
    }
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }

    const text = String(this.text || '').trim();
    const hasImage = !!this.imageDataUrl;
    if (!text && !hasImage) {
      this.err = '请输入描述，或选择一张截图。';
      m.redraw();
      return;
    }

    this.loading = true;
    this.err = null;
    this.missing = [];
    this.warnings = [];
    m.redraw();

    try {
      const api = app.forum.attribute('apiUrl');
      const r = await app.request({
        method: 'POST',
        url: `${api}/badminton/assistant/draft`,
        body: {
          input_type: hasImage ? 'image' : 'text',
          text,
          image: hasImage ? this.imageDataUrl : undefined,
          round_index: this.roundIndex + 1,
          previous_draft: this.latestDraft || {},
          context: { selectedVenueId: board.selectedVenueId, selectedDate: board.selectedDate },
        },
      });

      this.roundIndex = this.roundIndex + 1;
      this.missing = r?.missing || [];
      this.warnings = r?.warnings || [];
      this.remainingRounds = Math.max(0, Number(r?.remaining_rounds ?? this.maxRounds - this.roundIndex));
      this.latestDraft = r?.draft || this.latestDraft || {};
      if (text) this.assistantMsgs.push({ role: 'user', text });
      if (r?.assistant_reply) this.assistantMsgs.push({ role: 'assistant', text: String(r.assistant_reply) });
      if (this.assistantMsgs.length > 8) this.assistantMsgs = this.assistantMsgs.slice(-8);

      const shouldFill = !!r?.should_fill;
      if (shouldFill) {
        board.applyAssistantDraft(this.latestDraft || {}, { missing: this.missing, warnings: this.warnings });
      }

      this.loading = false;
      if (shouldFill) {
        this.open = false;
      }
      // 清理输入，进入下一轮（或结束）
      this.text = '';
      this.imageDataUrl = '';
      m.redraw();
    } catch (e) {
      this.loading = false;
      this.err = '生成失败，请稍后重试。';
      m.redraw();
    }
  }

  view(vnode) {
    const board = vnode.attrs.board;
    this._board = board;
    const topBase = 'var(--header-height, 0px)';
    const style = `transform: translate3d(${Math.round(this.pos.x)}px, ${Math.round(
      this.pos.y
    )}px, 0); top: calc(${topBase} + 8px); left: 0;`;
    const placeholderConfigured = String(app.forum.attribute('acme-pku-badminton.assistant_placeholder') || '').trim();
    const textareaPlaceholder =
      this.remainingRounds > 0 ? placeholderConfigured : '对话轮数已用尽，将直接生成草稿';

    return (
      <div className={`badminton-assistant-float ${this.open ? 'open' : ''}`} style={style}>
        <button
          type="button"
          className="badminton-assistant-float-btn"
          onpointerdown={(e) => this.onPointerDown(e)}
          onpointermove={(e) => this.onPointerMove(e)}
          onpointerup={() => this.onPointerUp()}
          onpointercancel={() => this.onPointerUp()}
          ontouchstart={(e) => this.onPointerDown(e)}
          ontouchmove={(e) => this.onPointerMove(e)}
          ontouchend={() => this.onPointerUp()}
          onclick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // 拖动结束会触发 click：用 dragMoved 过滤
            if (this.dragMoved) return;
            this.toggleOpen();
            m.redraw();
          }}
          title="填写助手"
        >
          填写助手
        </button>

        {this.open ? (
          <div className="badminton-assistant-float-panel" onclick={(e) => e.stopPropagation()}>
            <div className="badminton-assistant-float-panel-head">
              <div className="badminton-assistant-float-title">填写助手</div>
              <button
                type="button"
                className="badminton-assistant-float-close"
                onclick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.open = false;
                  m.redraw();
                }}
                aria-label="关闭"
              >
                ×
              </button>
            </div>

            <div className="badminton-assistant-float-meta" style="padding-top: 8px;">
              剩余对话轮数：{this.remainingRounds}
            </div>

            {this.assistantMsgs.length ? (
              <div className="badminton-assistant-float-meta" style="padding-top: 6px; max-height: 120px; overflow: auto;">
                {this.assistantMsgs.map((msg, idx) => (
                  <div key={`${idx}-${msg.role}`} style="margin-bottom: 6px;">
                    <strong>{msg.role === 'user' ? '你' : '助手'}：</strong>
                    <span>{msg.text}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="badminton-assistant-float-inputRow">
              <textarea
                className="FormControl badminton-assistant-float-textarea"
                rows="3"
                value={this.text}
                oninput={(e) => {
                  this.text = e.target.value;
                }}
                placeholder={textareaPlaceholder}
              />
            </div>

            {this.err ? <div className="badminton-assistant-float-err">{this.err}</div> : null}

            <div className="badminton-assistant-float-actions">
              <div className="badminton-assistant-float-left">
                <Button
                  className={classList('Button', 'badminton-assistant-float-iconBtn', {
                    'is-recording': this.recording,
                  })}
                  type="button"
                  icon="fas fa-microphone"
                  onclick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (this.recording) {
                      this.stopRecordingAndSend();
                    } else {
                      this.startRecording();
                    }
                  }}
                  title={
                    this.recording
                      ? `录音中（${this.recordSecs}s），点击结束并识别`
                      : '语音识别（≤60秒，结束后自动生成草稿）'
                  }
                  aria-label={this.recording ? `结束录音，已录 ${this.recordSecs} 秒` : '开始语音识别'}
                >
                  {this.recording ? (
                    <span className="badminton-assistant-wave" aria-hidden="true">
                      <span className="badminton-assistant-wave-bar" />
                      <span className="badminton-assistant-wave-bar" />
                      <span className="badminton-assistant-wave-bar" />
                      <span className="badminton-assistant-wave-bar" />
                    </span>
                  ) : null}
                </Button>

                <label
                  className={`badminton-assistant-float-upload ${this.imageDataUrl ? 'has-image' : ''}`}
                  title={this.imageDataUrl ? '已选择图片，点击可更换' : '添加图片'}
                >
                  <span className="Button badminton-assistant-float-iconBtn" role="button" tabindex="0">
                    {icon('fas fa-image')}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onchange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        this.imageDataUrl = String(reader.result || '');
                        m.redraw();
                      };
                      reader.readAsDataURL(f);
                    }}
                  />
                </label>
              </div>

              <div className="badminton-assistant-float-right">
                <Button
                  className="Button Button--primary"
                  type="button"
                  loading={this.loading}
                  disabled={this.loading}
                  onclick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.submitTextOrImage({ board });
                  }}
                >
                  发送
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** 与服务器撤下逻辑一致：统一 Asia/Shanghai（UTC+8） */
function beijingNowParts() {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(new Date());
  const g = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    ymd: `${g('year')}-${g('month')}-${g('day')}`,
    hm: `${g('hour')}:${g('minute')}`,
  };
}

function getNowDateHm() {
  const p = beijingNowParts();
  return { date: p.ymd, hm: p.hm };
}

function uiCalendarDays(rules) {
  const row = (rules || []).find((r) => r.rule_key === 'ui.calendar_days_ahead');
  if (!row) return 7;
  let raw = row.rule_value;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.min(14, Math.max(1, raw));
  }
  try {
    raw = JSON.parse(raw);
  } catch (e) {
    /* ignore */
  }
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? Math.min(14, Math.max(1, n)) : 7;
}

function buildDateList(dayCount) {
  const w = ['日', '一', '二', '三', '四', '五', '六'];
  const p = beijingNowParts();
  const [y0, m0, d0] = p.ymd.split('-').map((x) => parseInt(x, 10));
  const list = [];
  for (let i = 0; i < dayCount; i++) {
    const u = Date.UTC(y0, m0 - 1, d0 + i);
    const dd = new Date(u);
    const y = dd.getUTCFullYear();
    const mo = dd.getUTCMonth() + 1;
    const day = dd.getUTCDate();
    list.push({
      date: `${y}-${pad2(mo)}-${pad2(day)}`,
      dayName: `周${w[dd.getUTCDay()]}`,
      dateStr: `${mo}月${day}日`,
      isActive: i === 0,
    });
  }
  return list;
}

function tradeSlotIds(t) {
  const ids = [];
  if (t.trade_type === 'swap' && t.payload && Array.isArray(t.payload.swap_have?.slots)) {
    t.payload.swap_have.slots.forEach((s) => {
      if (!s) return;
      if (s.slot_id != null && s.slot_id !== '') {
        const sid = Number(s.slot_id);
        if (Number.isFinite(sid) && !ids.includes(sid)) ids.push(sid);
      }
    });
    return ids;
  }
  if (t.slot_id != null && t.slot_id !== '') {
    const one = Number(t.slot_id);
    if (Number.isFinite(one)) ids.push(one);
  }
  if (t.payload && Array.isArray(t.payload.slots)) {
    t.payload.slots.forEach((s) => {
      if (!s) return;
      if (s.slot_id != null && s.slot_id !== '') {
        const sid = Number(s.slot_id);
        if (Number.isFinite(sid) && !ids.includes(sid)) ids.push(sid);
      }
    });
  }
  return ids;
}

/** 交换：展示用 venues 名称解析 */
function venueNameById(config, id) {
  const n = Number(id);
  const v = (config?.venues || []).find((x) => Number(x.id) === n);
  return v?.name || `场馆#${id}`;
}

function swapSlotNames(config, venueId, tradeDate, slotIds) {
  const slots = (config?.timeSlots || []).filter(
    (s) => Number(s.venue_id || 0) === Number(venueId) && slotIds.some((id) => Number(id) === Number(s.id))
  );
  return slotIds.map((id) => {
    const s = slots.find((x) => Number(x.id) === Number(id));
    return s ? s.name || `${s.start_time}-${s.end_time}` : `#${id}`;
  });
}

function courtNameById(config, courtId) {
  const n = Number(courtId);
  if (!Number.isFinite(n) || n <= 0) return '';
  const c = (config?.courts || []).find((x) => Number(x.id) === n);
  return c?.name || `#${courtId}`;
}

/** 与后台 areVenueSlotIdsAdjacent 一致：按当前页 slot 行顺序排列已选 id */
function sortSwapSlotIdsByRows(slotIdStrings, slotRows) {
  if (!slotRows || slotRows.length === 0) return [...(slotIdStrings || [])];
  const set = new Set((slotIdStrings || []).map((x) => String(x)));
  return slotRows.map((r) => String(r.id)).filter((id) => set.has(id));
}

function areSwapSlotsAdjacent(slotIdStrings, slotRows) {
  const sorted = sortSwapSlotIdsByRows(slotIdStrings, slotRows);
  if (sorted.length <= 1) return true;
  if (sorted.length > 2) return false;
  const order = slotRows.map((r) => String(r.id));
  const p0 = order.indexOf(sorted[0]);
  const p1 = order.indexOf(sorted[1]);
  return p0 >= 0 && p1 >= 0 && Math.abs(p0 - p1) === 1;
}

function tradeTypeCol(t) {
  return TYPE_KEYS.indexOf(t.trade_type);
}
function tradeStatusLabel(s) {
  if (s === 'removed') return '已撤下';
  if (s === 'open') return '进行中';
  if (s === 'matched') return '已匹配';
  if (s === 'completed') return '已完成';
  if (s === 'invalid') return '已失效';
  return s || '—';
}

function subscriptionStatusLabel(s) {
  if (s === 'removed') return '已结束';
  if (s === 'open') return '进行中';
  return s || '—';
}

function subscriptionCourtLabel(sub, config) {
  if (!sub || typeof sub !== 'object') return '—';
  if (sub.court_edge_pref === 'edge') return '边场';
  if (sub.court_edge_pref === 'non_edge') return '非边场';
  if (sub.court_id != null && Number(sub.court_id) > 0) {
    return sub.court_name || courtNameById(config, sub.court_id) || `场地 #${sub.court_id}`;
  }
  return '不限';
}

/** 将价格钳到上限时的展示字符串（与服务器两位小数一致） */
function formatPriceAtMost(max) {
  if (!Number.isFinite(max)) return '';
  const r = Math.round(max * 100) / 100;
  return Number.isInteger(r) ? String(r) : r.toFixed(2);
}

/**
 * 场地交易主体 UI（快速发布用大号 Modal；订阅/撤下/查看确认/详情等用 App-composer）
 */
export default class BadmintonTradesBoard extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.loading = true;
    this.capabilities = null;
    this.config = null;
    this.trades = [];
    this.error = null;

    this.dateList = [];
    this.selectedDate = '';
    this.selectedVenueId = null;
    this.calendarDaysAhead = 7;

    this.counts = [];
    this.slotRows = [];

    this.selectedRow = -1;
    this.selectedCol = -1;
    this.detailTrades = [];

    this.toastOk = null;
    this.toastErr = null;

    this.showPublish = false;
    this.myTrades = [];
    this.myIntentTrades = [];
    this.mySubscriptions = [];
    this.assistantFilled = {};
    this.assistantLastMissing = [];
    this.assistantLastWarnings = [];
    this.publishDraft = {
      trade_type: 'sell',
      wechat_plain: '',
      trade_date: '',
      venue_id: '',
      slot_id: '',
      slot_ids: [],
      allow_split: true,
      court_id: '',
      court_edge_pref: '',
      price: '',
      max_intents: '0',
      content: '',
      swap_have_date: '',
      swap_have_venue_id: '',
      swap_have_slot_ids: [],
      swap_have_slot_courts: {},
      swap_want_date: '',
      swap_want_venue_id: '',
      swap_want_slot_ids: [],
      swap_want_slot_courts: {},
      group_has_court: false,
      group_missing_male: '0',
      group_missing_female: '1',
      group_skill_levels: [],
      group_play_type: 'any',
    };

    /** 出售：标准价合计上限（与后端 TradePricing 一致） */
    this.sellPriceCap = null;
    this.sellPriceCapLoading = false;
    this._sellCapRequestSeq = 0;
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.bootstrap();
  }

  goBack() {
    if (app.history.canGoBack()) {
      app.history.back();
    } else {
      m.route.set(app.route('index'));
    }
  }

  enabledVenues() {
    const v = this.config?.venues || [];
    return v.filter((x) => x.is_enabled !== 0 && x.is_enabled !== false);
  }

  currentVenue() {
    if (this.selectedVenueId == null) return null;
    return this.enabledVenues().find((v) => Number(v.id) === Number(this.selectedVenueId));
  }

  /** 场馆下可选的具体场地（不因边场/非边场展示项而筛选列表） */
  courtsForVenue(vid) {
    if (vid == null || vid === '') return [];
    return (this.config?.courts || []).filter((c) => Number(c.venue_id) === Number(vid) && c.is_enabled !== 0);
  }

  /** 发布表单是否展示「边场/非边场」对外说明项（与具体场地号仍二选一互斥） */
  publishVenueAllowsEdge() {
    if (this.publishDraft.trade_type === 'group' && !this.publishDraft.group_has_court) {
      return false;
    }
    const vid =
      this.publishDraft.trade_type === 'swap' ? this.publishDraft.swap_have_venue_id : this.publishDraft.venue_id;
    const v = (this.config?.venues || []).find((x) => String(x.id) === String(vid));
    if (!v) return false;
    return Number(v.allow_edge_filter) !== 0;
  }

  courtSelectValue() {
    if (this.publishDraft.court_edge_pref === 'edge') return '__edge__';
    if (this.publishDraft.court_edge_pref === 'non_edge') return '__non_edge__';
    return this.publishDraft.court_id === '' ? '' : String(this.publishDraft.court_id);
  }

  onCourtSelectChange(e) {
    const v = e.target.value;
    if (v === '__edge__') {
      this.publishDraft.court_edge_pref = 'edge';
      this.publishDraft.court_id = '';
    } else if (v === '__non_edge__') {
      this.publishDraft.court_edge_pref = 'non_edge';
      this.publishDraft.court_id = '';
    } else {
      this.publishDraft.court_edge_pref = '';
      this.publishDraft.court_id = v;
    }
    m.redraw();
  }

  swapHaveSlotRows() {
    return this.slotsForVenue(this.publishDraft.swap_have_venue_id, this.publishDraft.swap_have_date);
  }

  swapWantSlotRows() {
    return this.slotsForVenue(this.publishDraft.swap_want_venue_id, this.publishDraft.swap_want_date);
  }

  toggleSwapHaveSlot(sidRaw, checked) {
    const sid = String(sidRaw);
    const rows = this.swapHaveSlotRows();
    let arr = [...(this.publishDraft.swap_have_slot_ids || [])];
    if (checked) {
      if (arr.includes(sid)) return;
      if (arr.length >= 2) return;
      arr.push(sid);
    } else {
      arr = arr.filter((x) => x !== sid);
      delete this.publishDraft.swap_have_slot_courts[sid];
    }
    arr = sortSwapSlotIdsByRows(arr, rows);
    this.publishDraft.swap_have_slot_ids = arr;
    if (arr.length === 0) {
      this.publishDraft.swap_want_slot_ids = [];
      this.publishDraft.swap_want_slot_courts = {};
      this.publishDraft.swap_have_slot_courts = {};
    } else if (this.publishDraft.swap_want_slot_ids.length > arr.length) {
      const trimmed = this.publishDraft.swap_want_slot_ids.slice(0, arr.length);
      this.publishDraft.swap_want_slot_ids = sortSwapSlotIdsByRows(trimmed, this.swapWantSlotRows());
      const nextW = { ...this.publishDraft.swap_want_slot_courts };
      Object.keys(nextW).forEach((k) => {
        if (!this.publishDraft.swap_want_slot_ids.includes(k)) delete nextW[k];
      });
      this.publishDraft.swap_want_slot_courts = nextW;
    }
    m.redraw();
  }

  toggleSwapWantSlot(sidRaw, checked) {
    const maxN = this.publishDraft.swap_have_slot_ids.length;
    if (maxN < 1) return;
    const rows = this.swapWantSlotRows();
    const sid = String(sidRaw);
    let arr = [...(this.publishDraft.swap_want_slot_ids || [])];
    if (checked) {
      if (arr.includes(sid)) return;
      if (arr.length >= maxN) return;
      arr.push(sid);
    } else {
      arr = arr.filter((x) => x !== sid);
      delete this.publishDraft.swap_want_slot_courts[sid];
    }
    this.publishDraft.swap_want_slot_ids = arr;
    m.redraw();
  }

  swapVenueAllowsEdge(venueIdStr) {
    const v = (this.config?.venues || []).find((x) => String(x.id) === String(venueIdStr));
    if (!v) return false;
    return Number(v.allow_edge_filter) !== 0;
  }

  swapSlotCourtSelectValue(map, sid) {
    const v = map[sid];
    if (v === '__edge__') return '__edge__';
    if (v === '__non_edge__') return '__non_edge__';
    return v === undefined || v === null || v === '' ? '' : String(v);
  }

  setSwapSlotCourtMap(mapKey, sid, val) {
    const courtMap = this.publishDraft[mapKey];
    if (val === '' || val === '__clear__') delete courtMap[sid];
    else courtMap[sid] = val;
    m.redraw();
  }

  renderSwapSlotCourtSelect(mapKey, sid, venueIdStr) {
    const map = this.publishDraft[mapKey];
    const allows = this.swapVenueAllowsEdge(venueIdStr);
    const val = this.swapSlotCourtSelectValue(map, sid);
    const rows = this.courtsForVenue(venueIdStr);
    return (
      <select
        className="FormControl"
        value={val}
        onchange={(e) => this.setSwapSlotCourtMap(mapKey, sid, e.target.value)}
      >
        <option value="">不指定</option>
        {allows ? (
          <optgroup label="对他人展示为（不计入具体场地号）">
            <option value="__edge__">{edgeCourtOptionLabelForPublish(this.config, venueIdStr)}</option>
            <option value="__non_edge__">非边场</option>
          </optgroup>
        ) : null}
        {rows.length ? (
          <optgroup label="具体场地">
            {rows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
    );
  }

  buildSwapPublishBody() {
    const d = this.publishDraft;
    const hvid = parseInt(d.swap_have_venue_id, 10);
    const wvid = parseInt(d.swap_want_venue_id, 10);
    const haveRows = this.slotsForVenue(d.swap_have_venue_id, d.swap_have_date);
    const wantRows = this.slotsForVenue(d.swap_want_venue_id, d.swap_want_date);
    const haveOrder = sortSwapSlotIdsByRows(d.swap_have_slot_ids, haveRows);
    const wantOrder = (d.swap_want_slot_ids || []).map(String);
    const mapHavePiece = (sid) => {
      const raw = d.swap_have_slot_courts[sid];
      return parseSwapSlotMapSelection(raw);
    };
    const mapWantPiece = (sid) => parseSwapSlotMapSelection(d.swap_want_slot_courts[sid]);
    const haveSlots = haveOrder.map((sid) => {
      const piece = mapHavePiece(sid);
      const courtLabel = formatSwapSlotCourtLabel(this.config, hvid, {
        court_id: piece.court_id,
        court_edge_pref: piece.court_edge_pref,
      });
      const row = {
        venue_id: hvid,
        slot_id: parseInt(sid, 10),
        court_id: piece.court_id,
        court_edge_pref: piece.court_edge_pref,
        slot_text: haveRows.find((s) => Number(s.id) === Number(sid))?.name || '',
        court_name: piece.court_id ? courtNameById(this.config, piece.court_id) : '',
        court_label: courtLabel,
      };
      return row;
    });
    const wantSlots = wantOrder.map((sid) => {
      const piece = mapWantPiece(sid);
      const courtLabel = formatSwapSlotCourtLabel(this.config, wvid, {
        court_id: piece.court_id,
        court_edge_pref: piece.court_edge_pref,
      });
      return {
        venue_id: wvid,
        slot_id: parseInt(sid, 10),
        court_id: piece.court_id,
        court_edge_pref: piece.court_edge_pref,
        slot_text: wantRows.find((s) => Number(s.id) === Number(sid))?.name || '',
        court_name: piece.court_id ? courtNameById(this.config, piece.court_id) : '',
        court_label: courtLabel,
      };
    });
    const hvName = venueNameById(this.config, hvid);
    const wvName = venueNameById(this.config, wvid);
    const hn = haveOrder.map((sid) => {
      const slotLabel = swapSlotNames(this.config, hvid, d.swap_have_date, [sid])[0];
      const lbl = formatSwapSlotCourtLabel(this.config, hvid, mapHavePiece(sid));
      return `${slotLabel}·${lbl}`;
    });
    const wn = wantOrder.map((sid) => {
      const slotLabel = swapSlotNames(this.config, wvid, d.swap_want_date, [sid])[0];
      const lbl = formatSwapSlotCourtLabel(this.config, wvid, mapWantPiece(sid));
      return `${slotLabel}·${lbl}`;
    });
    const merged = `换出 ${d.swap_have_date} ${hvName} ${hn.join('、')}；换入 ${d.swap_want_date} ${wvName} ${wn.join('、')}`;
    const first = haveSlots[0];
    const firstCourt = first && first.court_id ? first.court_id : null;
    const body = {
      trade_type: 'swap',
      trade_date: d.swap_have_date,
      title: '',
      wechat_plain: d.wechat_plain,
      content: d.content,
      max_intents: parseInt(d.max_intents, 10) || 0,
      venue_id: hvid,
      slot_id: parseInt(haveOrder[0], 10),
      payload: {
        swap_have: { trade_date: d.swap_have_date, venue_id: hvid, slots: haveSlots },
        swap_want: { trade_date: d.swap_want_date, venue_id: wvid, slots: wantSlots },
        slots: haveSlots,
        merged_slots_text: merged,
      },
    };
    if (firstCourt) body.court_id = firstCourt;
    return body;
  }

  toggleGroupHasCourt(has) {
    this.publishDraft.group_has_court = !!has;
    if (!this.publishDraft.group_has_court) {
      this.publishDraft.court_id = '';
      this.publishDraft.court_edge_pref = '';
    }
    m.redraw();
  }

  toggleGroupSkillLevel(stepVal, checked) {
    const s = String(stepVal);
    let arr = [...(this.publishDraft.group_skill_levels || [])];
    if (checked) {
      if (!arr.includes(s)) arr.push(s);
    } else {
      arr = arr.filter((x) => x !== s);
    }
    arr.sort((a, b) => Number(a) - Number(b));
    this.publishDraft.group_skill_levels = arr;
    m.redraw();
  }

  buildGroupPublishBody() {
    const d = this.publishDraft;
    const tradeDate = d.trade_date || this.selectedDate;
    const vid = parseInt(d.venue_id, 10);
    const sid = parseInt(d.slot_id, 10);
    const rows = this.slotsForVenue(d.venue_id, tradeDate);
    const slotRow = rows.find((s) => Number(s.id) === sid);
    const hasCourt = !!d.group_has_court;
    const edgePref =
      hasCourt && (d.court_edge_pref === 'edge' || d.court_edge_pref === 'non_edge') ? d.court_edge_pref : '';
    const resolvedCourtId = hasCourt && !edgePref && d.court_id !== '' ? parseInt(d.court_id, 10) : null;
    const missM = parseInt(d.group_missing_male, 10);
    const missF = parseInt(d.group_missing_female, 10);
    const levels = (d.group_skill_levels || [])
      .map((x) => parseFloat(String(x)))
      .filter((n) => Number.isFinite(n));
    const playLabel = groupPlayTypeLabel(d.group_play_type);
    const levelsStr = levels
      .map((n) => (Math.abs(n % 1) < 1e-6 ? String(Math.round(n)) : String(n)))
      .join('/');
    const slotBit = slotRow ? ` · ${slotRow.name || `${slotRow.start_time}-${slotRow.end_time}`}` : '';
    const merged = `组局 · 缺男生${missM}人、女生${missF}人 · ${playLabel} · 中羽${levelsStr} · ${hasCourt ? '已订场' : '未订场'}${slotBit}`;

    const payload = {
      group_has_court: hasCourt,
      group_missing_male: missM,
      group_missing_female: missF,
      group_skill_levels: levels,
      group_play_type: d.group_play_type,
      merged_slots_text: merged,
    };
    if (hasCourt && edgePref) payload.court_edge_pref = edgePref;

    const body = {
      trade_type: 'group',
      trade_date: tradeDate,
      title: '',
      wechat_plain: d.wechat_plain,
      content: d.content,
      max_intents: parseInt(d.max_intents, 10) || 0,
      venue_id: vid,
      slot_id: sid,
      payload,
    };
    if (hasCourt) {
      if (edgePref) body.court_edge_pref = edgePref;
      if (resolvedCourtId && !edgePref && Number.isFinite(resolvedCourtId)) body.court_id = resolvedCourtId;
    }
    return body;
  }

  slotsForVenue(vid, forDate = this.selectedDate) {
    if (vid == null || vid === '') return [];
    const now = getNowDateHm();
    return (this.config?.timeSlots || [])
      .filter((s) => Number(s.venue_id || 0) === Number(vid))
      .filter((s) => s.is_enabled !== 0 && s.is_enabled !== false)
      .filter((s) => {
        if (forDate !== now.date) return true;
        const end = String(s.end_time || '').slice(0, 5);
        if (!end) return true;
        return end > now.hm;
      })
      .slice()
      .sort((a, b) => Number(a.sort) - Number(b.sort));
  }

  refreshDateListForVenue() {
    const full = buildDateList(this.calendarDaysAhead || 7);
    const vid = this.selectedVenueId;
    const filtered = vid == null ? full : full.filter((d) => this.slotsForVenue(vid, d.date).length > 0);
    this.dateList = filtered;

    const keep = this.selectedDate && this.dateList.some((d) => d.date === this.selectedDate);
    const next = keep ? this.selectedDate : this.dateList[0]?.date || '';
    this.selectedDate = next;
    this.dateList.forEach((d) => {
      d.isActive = next ? d.date === next : false;
    });
    if (this.selectedDate) this.publishDraft.trade_date = this.selectedDate;
  }

  recomputeSlotsAndCounts() {
    const vid = this.selectedVenueId;
    const now = getNowDateHm();
    const slots = (this.config?.timeSlots || [])
      .filter((s) => {
        if (s.venue_id == null || Number(s.venue_id) === 0) return true;
        if (vid == null) return true;
        return Number(s.venue_id) === Number(vid);
      })
      .filter((s) => s.is_enabled !== 0 && s.is_enabled !== false)
      .filter((s) => {
        if (this.selectedDate !== now.date) return true;
        const end = String(s.end_time || '').slice(0, 5);
        if (!end) return true;
        return end > now.hm;
      })
      .slice()
      .sort((a, b) => Number(a.sort) - Number(b.sort));

    this.slotRows = slots;

    const rows = slots.length;
    const counts = [];
    for (let r = 0; r < rows; r++) {
      counts.push([0, 0, 0, 0]);
    }

    (this.trades || []).forEach((t) => {
      if (t.trade_date !== this.selectedDate) return;
      if (vid != null && Number(t.venue_id) !== Number(vid)) return;
      const col = tradeTypeCol(t);
      if (col < 0) return;
      tradeSlotIds(t).forEach((sid) => {
        const row = slots.findIndex((s) => Number(s.id) === Number(sid));
        if (row >= 0) counts[row][col]++;
      });
    });

    this.counts = counts;
  }

  selectDate(index) {
    this.dateList.forEach((d, i) => {
      d.isActive = i === index;
    });
    this.selectedDate = this.dateList[index].date;
    this.selectedRow = -1;
    this.selectedCol = -1;
    this.detailTrades = [];
    this.loadTrades();
  }

  selectVenue(id) {
    this.selectedVenueId = id;
    this.selectedRow = -1;
    this.selectedCol = -1;
    this.detailTrades = [];
    this.refreshDateListForVenue();
    this.recomputeSlotsAndCounts();
    const v = this.currentVenue();
    if (v) {
      this.publishDraft.venue_id = String(v.id);
    } else {
      this.publishDraft.venue_id = '';
    }
    this.publishDraft.slot_id = '';
    this.publishDraft.slot_ids = [];
    this.publishDraft.court_edge_pref = '';
    this.publishDraft.court_id = '';
    this.loadTrades();
    m.redraw();
  }

  onCellClick(row, col) {
    this.selectedRow = row;
    this.selectedCol = col;
    const type = TYPE_KEYS[col];
    const slot = this.slotRows[row];
    const hourLabel = slot ? `${slot.start_time}-${slot.end_time}` : '';

    const vid = this.selectedVenueId;
    this.detailTrades = (this.trades || []).filter((t) => {
      if (t.trade_date !== this.selectedDate || t.trade_type !== type) return false;
      if (vid != null && Number(t.venue_id) !== Number(vid)) return false;
      const sids = tradeSlotIds(t);
      return sids.some((sid) => Number(sid) === Number(slot.id));
    });

    this.detailTitle = `${hourLabel} · ${TYPE_LABELS[type]}（${this.detailTrades.length}条）`;
    m.redraw();
  }

  openPublishType(type) {
    this.showPublish = true;
    this.publishDraft.trade_type = type;
    const v = this.currentVenue();
    if (v) this.publishDraft.venue_id = String(v.id);
    this.publishDraft.trade_date = this.selectedDate;
    this.publishDraft.slot_id = '';
    this.publishDraft.slot_ids = [];
    this.publishDraft.allow_split = true;
    this.publishDraft.court_edge_pref = '';
    this.publishDraft.court_id = '';
    if (type === 'swap') {
      this.publishDraft.swap_have_date = this.selectedDate;
      this.publishDraft.swap_have_venue_id = v ? String(v.id) : this.publishDraft.venue_id || '';
      this.publishDraft.swap_have_slot_ids = [];
      this.publishDraft.swap_have_slot_courts = {};
      this.publishDraft.swap_want_date = this.selectedDate;
      this.publishDraft.swap_want_venue_id = v ? String(v.id) : this.publishDraft.venue_id || '';
      this.publishDraft.swap_want_slot_ids = [];
      this.publishDraft.swap_want_slot_courts = {};
    }
    if (type === 'group') {
      this.publishDraft.group_has_court = false;
      this.publishDraft.group_missing_male = '0';
      this.publishDraft.group_missing_female = '1';
      this.publishDraft.group_skill_levels = [];
      this.publishDraft.group_play_type = 'any';
    }
    this.toastOk = null;
    this.toastErr = null;
    if (type !== 'sell' && type !== 'buy') {
      this.sellPriceCap = null;
      this.sellPriceCapLoading = false;
    }
    m.redraw();
    if (type === 'sell' || type === 'buy') {
      void this.refreshSellPriceCap();
    }
    app.modal.show(BadmintonPublishTradeModal, { board: this });
  }

  showTradeDetailModal(trade, publisherWechat) {
    app.composer.load(TradeDetailComposer, { trade, publisherWechat, config: this.config });
    app.composer.show();
  }

  applyIntentResponseToTradeInList(t, resp) {
    const id = Number(t.id);
    const idx = (this.trades || []).findIndex((x) => Number(x.id) === id);
    if (idx < 0 || !resp) return;
    const row = { ...this.trades[idx] };
    if (resp.intentCount != null) row.intent_count = resp.intentCount;
    if (resp.maxIntents != null) row.max_intents = resp.maxIntents;
    if (resp.remainingIntents !== undefined) row.remaining_intents = resp.remainingIntents;
    if (resp.tradeStatus) row.status = resp.tradeStatus;
    this.trades[idx] = row;
  }

  refreshDetailTradesFromGrid() {
    if (this.selectedRow < 0 || this.selectedCol < 0) return;
    const type = TYPE_KEYS[this.selectedCol];
    const slot = this.slotRows[this.selectedRow];
    const vid = this.selectedVenueId;
    this.detailTrades = (this.trades || []).filter((t) => {
      if (t.trade_date !== this.selectedDate || t.trade_type !== type) return false;
      if (vid != null && Number(t.venue_id) !== Number(vid)) return false;
      const sids = tradeSlotIds(t);
      return sids.some((sid) => Number(sid) === Number(slot.id));
    });
    this.detailTitle = `${slot ? `${slot.start_time}-${slot.end_time}` : ''} · ${TYPE_LABELS[type]}（${this.detailTrades.length}条）`;
  }

  findMyIntentTrade(tradeId) {
    return (this.myIntentTrades || []).find((x) => Number(x.id) === Number(tradeId));
  }

  confirmViewTradePrompt() {
    return new Promise((resolve) => {
      let settled = false;
      const done = (v) => {
        if (settled) return;
        settled = true;
        resolve(v);
      };
      app.composer.load(ViewTradeConfirmComposer, {
        onconfirm: () => done(true),
        onhide: () => done(false),
      });
      app.composer.show();
    });
  }

  async postTradeIntent(t) {
    const api = app.forum.attribute('apiUrl');
    return app.request({
      method: 'POST',
      url: `${api}/badminton/trades/${t.id}/intent`,
      body: {},
      errorHandler: silentFlarumRequestError,
    });
  }

  async handleTradeRowClick(t) {
    this.toastOk = null;
    this.toastErr = null;
    const user = app.session.user;
    if (!user) {
      try {
        const LogInModal = flarum.core.compat['forum/components/LogInModal'];
        app.modal.show(LogInModal);
      } catch (e) {
        const base = app.forum.attribute('baseUrl') || '';
        this.toastErr = (
          <span>
            需要先 <a href={`${base}/login`}>登录</a> 或 <a href={`${base}/register`}>注册</a> 才能查看具体信息与联系方式。
          </span>
        );
        m.redraw();
      }
      return;
    }
    const uid = user.id();
    const isOwner = String(t.user_id) === String(uid);
    if (isOwner) {
      const wx = t.wechat_plain || t.wechat_masked || '（以您发布时填写为准）';
      this.showTradeDetailModal(t, wx);
      return;
    }
    const fromIntent = this.findMyIntentTrade(t.id);
    if (fromIntent) {
      const wx = fromIntent.wechat_plain || fromIntent.wechat_masked || '';
      this.showTradeDetailModal(t, wx);
      return;
    }
    if (!this.capabilities.respondTrade) {
      this.toastErr = '你没有响应/查看联系方式的权限。';
      m.redraw();
      return;
    }
    const ok = await this.confirmViewTradePrompt();
    if (!ok) return;
    try {
      const resp = await this.postTradeIntent(t);
      if (isBadmintonFailureResponse(resp)) {
        if (resp.error === 'intent_duplicate' && resp.publisherWechat) {
          await this.loadMyTradeIntents();
          this.refreshDetailTradesFromGrid();
          this.showTradeDetailModal(t, resp.publisherWechat);
          m.redraw();
          return;
        }
        const detail = humanMessageFromBadmintonBody(resp) || '操作失败';
        this.pushBoardError(`无法查看：${detail}`);
        return;
      }
      this.applyIntentResponseToTradeInList(t, resp);
      await this.loadTrades();
      await this.loadMyTrades();
      await this.loadMyTradeIntents();
      this.refreshDetailTradesFromGrid();
      this.recomputeSlotsAndCounts();
      const wx = resp?.publisherWechat || t.wechat_masked || '';
      this.showTradeDetailModal(t, wx);
      m.redraw();
    } catch (e) {
      this.pushBoardError(`无法查看：${parseBadmintonRequestError(e)}`);
    }
  }

  async loadMyTradeIntents() {
    const api = app.forum.attribute('apiUrl');
    try {
      const r = await app.request({
        method: 'GET',
        url: `${api}/badminton/me/trade-intents`,
      });
      this.myIntentTrades = r?.data || [];
    } catch (e) {
      this.myIntentTrades = [];
    }
  }

  validatePublishDraft() {
    const d = this.publishDraft;
    if (!String(d.wechat_plain || '').trim()) {
      return '请填写微信号（必填）。';
    }
    if (d.trade_type === 'swap') {
      if (!d.swap_have_date || !d.swap_want_date) {
        return '交换须选择「拥有」与「希望换得」的日期。';
      }
      if (!d.swap_have_venue_id || !d.swap_want_venue_id) {
        return '请选择交换双方的场馆。';
      }
      const nh = (d.swap_have_slot_ids || []).length;
      const nw = (d.swap_want_slot_ids || []).length;
      if (nh < 1 || nh > 2) {
        return '「我拥有的」时段须选 1～2 个。';
      }
      if (nw < 1 || nw > nh) {
        return `「希望换得」时段须选 1～${nh} 个（不能超过拥有时段数）。`;
      }
      const haveRows = this.slotsForVenue(d.swap_have_venue_id, d.swap_have_date);
      if (!areSwapSlotsAdjacent(d.swap_have_slot_ids, haveRows)) {
        return '「我拥有的」若选两个时段，须在时间上相邻（按该场馆已列出的时段顺序连续）。';
      }
      const haveSorted = sortSwapSlotIdsByRows(d.swap_have_slot_ids, haveRows);
      for (const sid of haveSorted) {
        const raw = d.swap_have_slot_courts[sid];
        const piece = parseSwapSlotMapSelection(raw);
        if (piece.court_edge_pref === 'edge' || piece.court_edge_pref === 'non_edge') {
          if (!this.swapVenueAllowsEdge(d.swap_have_venue_id)) {
            return '当前「拥有」的场馆未开启此项，不能使用边场/非边场展示说明。';
          }
        }
      }
      for (const sid of d.swap_want_slot_ids || []) {
        const piece = parseSwapSlotMapSelection(d.swap_want_slot_courts[sid]);
        if (piece.court_edge_pref === 'edge' || piece.court_edge_pref === 'non_edge') {
          if (!this.swapVenueAllowsEdge(d.swap_want_venue_id)) {
            return '当前「换得」的场馆未开启此项，不能使用边场/非边场展示说明。';
          }
        }
      }
    } else if (d.trade_type === 'group') {
      if (d.venue_id === '' || d.venue_id == null) {
        return '请选择场馆（必填）。';
      }
      if (!d.slot_id || d.slot_id === '') {
        return '请选择一个时段（必填）。';
      }
      const missM = parseInt(d.group_missing_male, 10);
      const missF = parseInt(d.group_missing_female, 10);
      if (!Number.isFinite(missM) || missM < 0 || missM > 99 || !Number.isFinite(missF) || missF < 0 || missF > 99) {
        return '缺男生、缺女生人数须分别为 0～99 的整数。';
      }
      if (missM + missF < 1) {
        return '缺男生与缺女生人数之和至少为 1。';
      }
      if (!d.group_skill_levels || d.group_skill_levels.length < 1) {
        return '请至少选择一个希望匹配的中羽级别。';
      }
      if (!d.group_play_type || !GROUP_PLAY_TYPES.some((p) => p.id === d.group_play_type)) {
        return '请选择组局类型。';
      }
    } else {
      if (d.venue_id === '' || d.venue_id == null) {
        return '请选择场馆（必填）。';
      }
      if (d.trade_type === 'sell' || d.trade_type === 'buy') {
        if (!d.slot_ids || d.slot_ids.length < 1) {
          return `${TYPE_LABELS[d.trade_type]}须至少勾选一个时段（必填）。`;
        }
      } else if (!d.slot_id || d.slot_id === '') {
        return '请选择一个时段（必填）。';
      }
    }
    if (d.trade_type === 'sell' || d.trade_type === 'buy') {
      const raw = String(d.price ?? '').trim();
      if (raw === '') {
        return `请填写${TYPE_LABELS[d.trade_type]}价格（必填）。`;
      }
      const p = parseFloat(d.price);
      if (!Number.isFinite(p) || p <= 0) {
        return `${TYPE_LABELS[d.trade_type]}价格须为大于 0 的有效数字。`;
      }
      if (this.sellPriceCap != null && Number.isFinite(this.sellPriceCap.max_price) && p > this.sellPriceCap.max_price) {
        const tier = TIER_LABELS[this.sellPriceCap.tier] || this.sellPriceCap.tier;
        return `${TYPE_LABELS[d.trade_type]}出价不能超过当前标准价合计上限 ¥${this.sellPriceCap.max_price}（${tier}）。`;
      }
    }
    return null;
  }

  /** 站内红条 + Flarum 全局 Alert + 滚动到提示（发布区在页面底部时仍可见） */
  pushBoardError(message) {
    if (!message) return;
    this.toastOk = null;
    this.toastErr = message;
    try {
      app.alerts.show({ type: 'error' }, message);
    } catch (err) {
      /* ignore */
    }
    m.redraw();
    setTimeout(() => {
      document.querySelector('.badminton-board .badminton-alert.err')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  /** 发布成功：绿条 + Flarum 成功 Alert */
  pushBoardSuccess(message) {
    if (!message) return;
    this.toastErr = null;
    this.toastOk = message;
    try {
      app.alerts.show({ type: 'success' }, message);
    } catch (err) {
      /* ignore */
    }
    m.redraw();
    setTimeout(() => {
      document.querySelector('.badminton-board .badminton-alert.ok')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }

  clampPublishPriceToCap() {
    if (!this.sellPriceCap || !Number.isFinite(this.sellPriceCap.max_price)) return;
    const max = this.sellPriceCap.max_price;
    const raw = String(this.publishDraft.price ?? '').trim();
    if (raw === '') return;
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > max) {
      this.publishDraft.price = formatPriceAtMost(max);
    }
  }

  async refreshSellPriceCap() {
    if (!this.showPublish || (this.publishDraft.trade_type !== 'sell' && this.publishDraft.trade_type !== 'buy')) {
      this.sellPriceCap = null;
      this.sellPriceCapLoading = false;
      return;
    }
    const tradeDate = this.publishDraft.trade_date || this.selectedDate;
    let ids = [];
    if (this.publishDraft.slot_ids && this.publishDraft.slot_ids.length > 0) {
      ids = this.publishDraft.slot_ids.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n) && n > 0);
    } else if (this.publishDraft.slot_id) {
      const one = parseInt(this.publishDraft.slot_id, 10);
      if (Number.isFinite(one) && one > 0) ids = [one];
    }
    if (!tradeDate || ids.length === 0) {
      this.sellPriceCap = null;
      this.sellPriceCapLoading = false;
      this.clampPublishPriceToCap();
      m.redraw();
      return;
    }

    const seq = ++this._sellCapRequestSeq;
    this.sellPriceCapLoading = true;
    m.redraw();

    const api = app.forum.attribute('apiUrl');
    const q = new URLSearchParams();
    q.set('tradeDate', tradeDate);
    q.set('slotIds', ids.join(','));

    try {
      const r = await app.request({
        method: 'GET',
        url: `${api}/badminton/me/max-sell-price?${q.toString()}`,
      });
      if (seq !== this._sellCapRequestSeq) return;
      if (r && r.ok === true && r.max_price != null) {
        this.sellPriceCap = {
          max_price: Number(r.max_price),
          tier: r.tier || 'student',
          student_base: r.student_base || {},
          staff_multiplier: r.staff_multiplier != null ? Number(r.staff_multiplier) : 2,
          alumni_multiplier: r.alumni_multiplier != null ? Number(r.alumni_multiplier) : 3,
          lines: Array.isArray(r.lines) ? r.lines : [],
          calendar_summary: r.calendar_summary || 'weekday_slot',
        };
        this.clampPublishPriceToCap();
      } else {
        this.sellPriceCap = null;
      }
    } catch (e) {
      if (seq !== this._sellCapRequestSeq) return;
      this.sellPriceCap = null;
    }
    this.sellPriceCapLoading = false;
    m.redraw();
  }

  async submitPublish() {
    this.toastOk = null;
    this.toastErr = null;
    if (this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy') {
      await this.refreshSellPriceCap();
    }
    const localErr = this.validatePublishDraft();
    if (localErr) {
      this.pushBoardError(localErr);
      return;
    }
    const api = app.forum.attribute('apiUrl');
    let body;
    if (this.publishDraft.trade_type === 'swap') {
      body = this.buildSwapPublishBody();
    } else if (this.publishDraft.trade_type === 'group') {
      body = this.buildGroupPublishBody();
    } else {
      body = {
        trade_type: this.publishDraft.trade_type,
        trade_date: this.publishDraft.trade_date || this.selectedDate,
        title: '',
        wechat_plain: this.publishDraft.wechat_plain,
        content: this.publishDraft.content,
        max_intents: parseInt(this.publishDraft.max_intents, 10) || 0,
      };
      if (this.publishDraft.venue_id !== '') {
        body.venue_id = parseInt(this.publishDraft.venue_id, 10);
      }
      if (this.publishDraft.slot_id !== '') {
        body.slot_id = parseInt(this.publishDraft.slot_id, 10);
      }
      const edgePref =
        this.publishDraft.court_edge_pref === 'edge' || this.publishDraft.court_edge_pref === 'non_edge'
          ? this.publishDraft.court_edge_pref
          : '';
      if (edgePref) {
        body.court_edge_pref = edgePref;
      }
      const resolvedCourtId = edgePref ? null : this.publishDraft.court_id !== '' ? parseInt(this.publishDraft.court_id, 10) : null;

      if ((this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy') && this.publishDraft.slot_ids.length > 0) {
        body.payload = {
          slots: this.publishDraft.slot_ids.map((sid) => ({
            venue_id: body.venue_id || null,
            slot_id: parseInt(sid, 10),
            court_id: resolvedCourtId,
            slot_text:
              (this.slotsForVenue(body.venue_id || this.publishDraft.venue_id, body.trade_date).find((s) => Number(s.id) === Number(sid))?.name || '') ||
              '',
          })),
        };
        body.allow_split = !!this.publishDraft.allow_split;
        body.slot_id = parseInt(this.publishDraft.slot_ids[0], 10);
      }
      if (resolvedCourtId && !edgePref) {
        body.court_id = resolvedCourtId;
      }
      if (this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy') {
        body.price = parseFloat(this.publishDraft.price);
      }
    }

    try {
      if (this.hasSellSlotBeyondNineHours()) {
        const okSell = await this.confirmSellNineHourPrompt();
        if (!okSell) return;
      }
      const resp = await app.request({
        method: 'POST',
        url: `${api}/badminton/trades`,
        body,
        errorHandler: silentFlarumRequestError,
      });
      if (isBadmintonFailureResponse(resp)) {
        const detail = humanMessageFromBadmintonBody(resp) || '服务器拒绝了本次发布。';
        this.pushBoardError(`发布失败：${detail}`);
        return;
      }
      this.pushBoardSuccess('发布成功。');
      await this.loadTrades();
      await this.loadMyTrades();
      app.modal.close();
      m.redraw();
    } catch (e) {
      this.pushBoardError(`发布失败：${parseBadmintonRequestError(e)}`);
    }
  }

  async loadTrades() {
    const api = app.forum.attribute('apiUrl');
    const r = await app.request({
      method: 'GET',
      url: `${api}/badminton/trades?tradeDate=${encodeURIComponent(this.selectedDate)}`,
    });
    this.trades = r?.data || [];
    this.recomputeSlotsAndCounts();
    m.redraw();
  }

  async loadMyTrades() {
    const api = app.forum.attribute('apiUrl');
    const user = app.session.user;
    if (!user) {
      this.myTrades = [];
      return;
    }
    try {
      const r = await app.request({
        method: 'GET',
        url: `${api}/badminton/me/trades`,
      });
      this.myTrades = r?.data || [];
    } catch (e) {
      this.myTrades = [];
    }
  }

  async loadMySubscriptions() {
    const api = app.forum.attribute('apiUrl');
    try {
      const r = await app.request({
        method: 'GET',
        url: `${api}/badminton/me/subscriptions`,
      });
      this.mySubscriptions = r?.data || [];
    } catch (e) {
      this.mySubscriptions = [];
    }
  }

  openSubscribeSlotModal(slot) {
    if (!this.capabilities?.viewTrade || !slot) return;
    if (!app.session.user) {
      try {
        const LogInModal = flarum.core.compat['forum/components/LogInModal'];
        app.modal.show(LogInModal);
      } catch (e) {
        this.toastErr = '需要先登录/注册后才能订阅。';
        m.redraw();
      }
      return;
    }
    if (!this.capabilities?.subscribeTrade) {
      this.toastErr = '你没有订阅场地动态的权限。';
      m.redraw();
      return;
    }
    app.composer.load(SubscribeSlotComposer, { board: this, slot });
    app.composer.show();
  }

  ensureLoginOrOpenPublish(type) {
    if (!app.session.user) {
      try {
        const LogInModal = flarum.core.compat['forum/components/LogInModal'];
        app.modal.show(LogInModal);
      } catch (e) {
        const base = app.forum.attribute('baseUrl') || '';
        this.toastErr = (
          <span>
            需要先 <a href={`${base}/login`}>登录</a> 或 <a href={`${base}/register`}>注册</a> 才能发布与操作。
          </span>
        );
        m.redraw();
      }
      return;
    }
    if (!this.capabilities?.createTrade) {
      this.toastErr = '你没有发布交易信息的权限。';
      m.redraw();
      return;
    }
    this.openPublishType(type);
  }

  openAssistant() {
    const enabled = app.forum.attribute('acme-pku-badminton.assistant_enabled') === '1';
    if (!enabled) {
      this.toastErr = '填写助手未开启。';
      m.redraw();
      return;
    }
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }
    app.modal.show(BadmintonAssistantModal, { board: this });
  }

  applyAssistantDraft(draft, meta = {}) {
    if (!draft || typeof draft !== 'object') return;
    this.assistantFilled = {};
    Object.keys(draft).forEach((k) => {
      this.assistantFilled[k] = true;
    });
    this.assistantLastMissing = meta.missing || [];
    this.assistantLastWarnings = meta.warnings || [];

    const d = this.publishDraft;
    if (draft.trade_type) d.trade_type = String(draft.trade_type);
    if (draft.trade_date) d.trade_date = String(draft.trade_date);
    if (draft.wechat_plain != null) d.wechat_plain = String(draft.wechat_plain);
    if (draft.venue_id != null) d.venue_id = String(draft.venue_id);
    if (draft.slot_id != null) d.slot_id = String(draft.slot_id);
    if (Array.isArray(draft.slot_ids)) d.slot_ids = draft.slot_ids.map((x) => String(x));
    if (draft.court_edge_pref != null) d.court_edge_pref = String(draft.court_edge_pref);
    if (draft.court_id != null) d.court_id = String(draft.court_id);
    if (draft.price != null) d.price = String(draft.price);
    if (draft.max_intents != null) d.max_intents = String(draft.max_intents);
    if (draft.content != null) d.content = String(draft.content);

    // 若助手未给日期，默认使用当前矩阵选中日期
    if (!d.trade_date) d.trade_date = this.selectedDate || d.trade_date;

    if (d.trade_type === 'swap') {
      if (draft.swap_have_date) d.swap_have_date = String(draft.swap_have_date);
      if (draft.swap_want_date) d.swap_want_date = String(draft.swap_want_date);
      if (draft.swap_have_venue_id != null) d.swap_have_venue_id = String(draft.swap_have_venue_id);
      if (draft.swap_want_venue_id != null) d.swap_want_venue_id = String(draft.swap_want_venue_id);
      if (Array.isArray(draft.swap_have_slot_ids)) d.swap_have_slot_ids = draft.swap_have_slot_ids.map((x) => String(x));
      if (Array.isArray(draft.swap_want_slot_ids)) d.swap_want_slot_ids = draft.swap_want_slot_ids.map((x) => String(x));
      if (draft.swap_have_slot_courts && typeof draft.swap_have_slot_courts === 'object') d.swap_have_slot_courts = draft.swap_have_slot_courts;
      if (draft.swap_want_slot_courts && typeof draft.swap_want_slot_courts === 'object') d.swap_want_slot_courts = draft.swap_want_slot_courts;
    }

    this.showPublish = true;
    m.redraw();

    // 打开“发布”弹窗让用户检查/补充/手动提交（禁止自动提交）
    app.modal.show(BadmintonPublishTradeModal, { board: this });
    if (d.trade_type === 'sell' || d.trade_type === 'buy') {
      void this.refreshSellPriceCap();
    }
  }

  async withdrawMySubscription(sub) {
    const id = Number(sub?.id || 0);
    if (id <= 0 || sub?.status !== 'open') return;
    const ok = window.confirm('确认取消这条订阅？取消后将不再收到对应条件的通知。');
    if (!ok) return;

    const api = app.forum.attribute('apiUrl');
    try {
      const resp = await app.request({
        method: 'POST',
        url: `${api}/badminton/subscriptions/${id}/withdraw`,
        body: { id },
        errorHandler: silentFlarumRequestError,
      });
      if (isBadmintonFailureResponse(resp)) {
        this.pushBoardError(humanMessageFromBadmintonBody(resp) || '取消失败');
        return;
      }
      this.toastOk = '已取消订阅。';
      await this.loadMySubscriptions();
      m.redraw();
    } catch (e) {
      this.pushBoardError(`取消失败：${parseBadmintonRequestError(e)}`);
    }
  }

  hasSellSlotBeyondNineHours() {
    if (this.publishDraft.trade_type !== 'sell' && this.publishDraft.trade_type !== 'buy') return false;
    const tradeDate = this.publishDraft.trade_date || this.selectedDate;
    const slotIds =
      this.publishDraft.slot_ids && this.publishDraft.slot_ids.length > 0
        ? this.publishDraft.slot_ids.map((x) => Number(x))
        : this.publishDraft.slot_id
        ? [Number(this.publishDraft.slot_id)]
        : [];
    if (slotIds.length === 0) return false;

    const slots = this.slotsForVenue(this.publishDraft.venue_id, tradeDate);
    const now = Date.now();
    const nineHoursMs = 9 * 60 * 60 * 1000;

    return slotIds.some((sid) => {
      const s = slots.find((x) => Number(x.id) === Number(sid));
      if (!s || !s.start_time) return false;
      const dt = new Date(`${tradeDate}T${String(s.start_time).slice(0, 5)}:00`);
      const ts = dt.getTime();
      if (!Number.isFinite(ts)) return false;
      return ts - now > nineHoursMs;
    });
  }

  confirmSellNineHourPrompt() {
    const tradeType = this.publishDraft.trade_type;
    return new Promise((resolve) => {
      let settled = false;
      const done = (v) => {
        if (settled) return;
        settled = true;
        resolve(v);
      };
      app.modal.show(SellNineHourConfirmModal, {
        tradeType,
        onconfirm: () => done(true),
        onhide: () => done(false),
      });
    });
  }

  confirmWithdraw(trade) {
    return new Promise((resolve) => {
      let settled = false;
      const done = (v) => {
        if (settled) return;
        settled = true;
        resolve(v);
      };
      app.composer.load(BadmintonWithdrawConfirmComposer, {
        trade,
        onconfirm: () => done(true),
        onhide: () => done(false),
      });
      app.composer.show();
    });
  }

  async withdrawMyTrade(trade) {
    const id = Number(trade?.id || 0);
    if (id <= 0) return;
    const ok = await this.confirmWithdraw(trade);
    if (!ok) return;

    const api = app.forum.attribute('apiUrl');
    try {
      const resp = await app.request({
        method: 'POST',
        url: `${api}/badminton/trades/${id}/withdraw`,
        body: { trade_id: id },
        errorHandler: silentFlarumRequestError,
      });
      if (isBadmintonFailureResponse(resp)) {
        const detail = humanMessageFromBadmintonBody(resp) || '操作失败';
        this.pushBoardError(`撤下失败：${detail}`);
        return;
      }
      this.toastOk = '已撤下该信息。';
      await this.loadMyTrades();
      await this.loadTrades();
      m.redraw();
    } catch (e) {
      this.pushBoardError(`撤下失败：${parseBadmintonRequestError(e)}`);
    }
  }

  async bootstrap() {
    try {
      this.loading = true;
      this.error = null;
      m.redraw();

      const api = app.forum.attribute('apiUrl');
      const capsResp = await app.request({
        method: 'GET',
        url: `${api}/badminton/me/capabilities`,
      });
      this.capabilities = capsResp?.capabilities || capsResp?.data?.capabilities || null;

      if (!this.capabilities?.viewTrade) {
        this.loading = false;
        m.redraw();
        return;
      }

      const cfgResp = await app.request({
        method: 'GET',
        url: `${api}/badminton/config`,
      });
      this.config = cfgResp?.data || null;
      const firstVenue = this.enabledVenues()[0];
      if (firstVenue) {
        this.selectedVenueId = Number(firstVenue.id);
        this.publishDraft.venue_id = String(firstVenue.id);
      }

      const days = uiCalendarDays(this.config?.rules);
      this.calendarDaysAhead = days;
      this.refreshDateListForVenue();
      if (!this.selectedDate) this.selectedDate = beijingNowParts().ymd;
      this.publishDraft.trade_date = this.selectedDate;

      this.recomputeSlotsAndCounts();
      await this.loadTrades();
      if (app.session.user) {
        await this.loadMyTrades();
        await this.loadMyTradeIntents();
        await this.loadMySubscriptions();
      } else {
        this.myTrades = [];
        this.myIntentTrades = [];
        this.mySubscriptions = [];
      }

      this.loading = false;
      m.redraw();
    } catch (e) {
      this.loading = false;
      this.error = '加载失败，请稍后重试。';
      m.redraw();
    }
  }


  /**
   * 发布表单。默认外层用于 App-composer；Modal 传入 outerClassName（含 Modal-body）
   */
  renderPublishFormContent(opts = {}) {
    const includeSubmitButton = opts.includeSubmitButton !== false;
    const outerClass =
      opts.outerClassName != null ? opts.outerClassName : 'badminton-publish-composer-inner badminton-publish-sheet';
    const venues = this.enabledVenues();
    const publishSlots = this.slotsForVenue(this.publishDraft.venue_id, this.publishDraft.trade_date || this.selectedDate);
    return (
      <div className={outerClass}>
              <p className="badminton-publish-legend">
                <abbr className="badminton-req" title="必填">
                  *
                </abbr>{' '}
                {this.publishDraft.trade_type === 'swap' ? (
                  <>
                    交换不填价格；选换出/换入的日期、场馆与时段。换出最多 2 个时段且若选 2
                    个须在时间上相邻；换入个数不超过换出，<strong>换入时段不要求相邻</strong>。每档可选不指定、对他人展示为边场/非边场（仅文字、非筛场地）或具体场地号（视后台是否允许边场说明）。
                  </>
                ) : this.publishDraft.trade_type === 'group' ? (
                  <>
                    组局：先选日期、场馆与时段，再选择是否已订场；仅当已拥有场地时可在「选填」中填写场地编号。须填写缺男生/缺女生人数、希望匹配的中羽级别（多选）与组局类型。
                  </>
                ) : (
                  <>表示必填；请务必先选场馆再选时段。</>
                )}
              </p>

              <div className="badminton-form-section">
                <div className="badminton-form-section-title">必填</div>
                {this.publishDraft.trade_type === 'swap' ? (
                  <div className="badminton-form-grid badminton-form-grid--publish">
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '微信号', '仅意向双方可见，请填写真实可添加的微信号')}
                      <input
                        className={`FormControl${this.assistantFilled.wechat_plain ? ' badminton-assistant-filled' : ''}`}
                        type="text"
                        autocomplete="off"
                        placeholder="例如：wxid_…"
                        value={this.publishDraft.wechat_plain}
                        oninput={(e) => {
                          this.publishDraft.wechat_plain = e.target.value;
                          m.redraw();
                        }}
                      />
                    </label>

                    <div className="badminton-form-field badminton-form-field--full">
                      <div className="badminton-form-section-title" style="margin: 4px 0 8px; font-size: 13px;">
                        我拥有的（换出）
                      </div>
                    </div>
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '拥有 · 日期', '该日期与矩阵列表日期一致时，会出现在对应列的格子统计中')}
                      <select
                        className="FormControl"
                        value={this.publishDraft.swap_have_date || this.selectedDate}
                        onchange={(e) => {
                          this.publishDraft.swap_have_date = e.target.value;
                          this.publishDraft.swap_have_slot_ids = [];
                          this.publishDraft.swap_have_slot_courts = {};
                          if ((this.publishDraft.swap_want_slot_ids || []).length > 0) {
                            this.publishDraft.swap_want_slot_ids = [];
                            this.publishDraft.swap_want_slot_courts = {};
                          }
                          m.redraw();
                        }}
                      >
                        {this.dateList.map((d) => (
                          <option key={d.date} value={d.date}>
                            {d.dayName} {d.dateStr}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '拥有 · 场馆', '')}
                      <select
                        className="FormControl"
                        value={this.publishDraft.swap_have_venue_id}
                        onchange={(e) => {
                          this.publishDraft.swap_have_venue_id = e.target.value;
                          this.publishDraft.swap_have_slot_ids = [];
                          this.publishDraft.swap_have_slot_courts = {};
                          this.publishDraft.court_id = '';
                          this.publishDraft.court_edge_pref = '';
                          if ((this.publishDraft.swap_want_slot_ids || []).length > 0) {
                            this.publishDraft.swap_want_slot_ids = [];
                            this.publishDraft.swap_want_slot_courts = {};
                          }
                          m.redraw();
                        }}
                      >
                        <option value="">请选择场馆</option>
                        {venues.map((vn) => (
                          <option key={vn.id} value={vn.id}>
                            {vn.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="badminton-form-field badminton-form-field--full">
                      {publishFieldLabel(true, '拥有 · 时段（最多 2 个）', '与上方日期、场馆对应')}
                      {this.swapHaveSlotRows().length === 0 ? (
                        <div className="badminton-field-help badminton-field-help--muted">
                          {this.publishDraft.swap_have_venue_id
                            ? '该场馆在所选日期下没有可用时段（可能已全部结束，或后台尚未配置）。'
                            : '请先选择「拥有」的场馆。'}
                        </div>
                      ) : (
                        <div className="badminton-slot-checkboxes">
                          {this.swapHaveSlotRows().map((s) => (
                            <label key={s.id} className="badminton-slot-checkbox-row">
                              <input
                                type="checkbox"
                                checked={(this.publishDraft.swap_have_slot_ids || []).includes(String(s.id))}
                                onchange={(e) => this.toggleSwapHaveSlot(s.id, e.target.checked)}
                              />
                              <span className="badminton-slot-checkbox-text">
                                {s.name || `${s.start_time}-${s.end_time}`}{' '}
                                <span className="badminton-slot-checkbox-time">
                                  ({s.start_time}–{s.end_time})
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="badminton-form-field badminton-form-field--full">
                      {(this.publishDraft.swap_have_slot_ids || []).length > 0 ? (
                        <>
                          {publishFieldLabel(
                            false,
                            '拥有 · 各时段场地',
                            '与出售相同：可不指定；若场馆开启，可选对他人展示为边场/非边场（不筛具体场地列表），或点选具体场地号。若勾选两个「拥有」时段，二者须在列表中相邻。'
                          )}
                          <div
                            className="badminton-form-grid badminton-form-grid--publish"
                            style="margin-top: 8px;"
                          >
                            {sortSwapSlotIdsByRows(
                              this.publishDraft.swap_have_slot_ids,
                              this.swapHaveSlotRows()
                            ).map((sid) => {
                              const row = this.swapHaveSlotRows().find((s) => String(s.id) === sid);
                              const slotLabel =
                                row?.name || (row ? `${row.start_time}–${row.end_time}` : `#${sid}`);
                              return (
                                <label key={`swap-have-court-${sid}`} className="badminton-form-field">
                                  <span
                                    className="badminton-field-help"
                                    style="display:block;margin-bottom:4px;"
                                  >
                                    {slotLabel}
                                  </span>
                                  {this.renderSwapSlotCourtSelect(
                                    'swap_have_slot_courts',
                                    sid,
                                    this.publishDraft.swap_have_venue_id
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </>
                      ) : null}
                    </div>

                    <div className="badminton-form-field badminton-form-field--full">
                      <div className="badminton-form-section-title" style="margin: 4px 0 8px; font-size: 13px;">
                        希望换得（换入）
                      </div>
                    </div>
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '换得 · 日期', '')}
                      <select
                        className="FormControl"
                        value={this.publishDraft.swap_want_date || this.selectedDate}
                        onchange={(e) => {
                          this.publishDraft.swap_want_date = e.target.value;
                          this.publishDraft.swap_want_slot_ids = [];
                          this.publishDraft.swap_want_slot_courts = {};
                          m.redraw();
                        }}
                      >
                        {this.dateList.map((d) => (
                          <option key={d.date} value={d.date}>
                            {d.dayName} {d.dateStr}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '换得 · 场馆', '')}
                      <select
                        className="FormControl"
                        value={this.publishDraft.swap_want_venue_id}
                        onchange={(e) => {
                          this.publishDraft.swap_want_venue_id = e.target.value;
                          this.publishDraft.swap_want_slot_ids = [];
                          this.publishDraft.swap_want_slot_courts = {};
                          m.redraw();
                        }}
                      >
                        <option value="">请选择场馆</option>
                        {venues.map((vn) => (
                          <option key={vn.id} value={vn.id}>
                            {vn.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="badminton-form-field badminton-form-field--full">
                      {publishFieldLabel(
                        true,
                        `换得 · 时段（可多选，至多 ${Math.max(1, (this.publishDraft.swap_have_slot_ids || []).length)} 个）`,
                        (this.publishDraft.swap_have_slot_ids || []).length < 1
                          ? '请先勾选「拥有」的时段'
                          : '数量不得超过已选拥有时段数'
                      )}
                      {(this.publishDraft.swap_have_slot_ids || []).length < 1 ? (
                        <div className="badminton-field-help badminton-field-help--muted">请先在上文选择至少一个「拥有」时段。</div>
                      ) : this.swapWantSlotRows().length === 0 ? (
                        <div className="badminton-field-help badminton-field-help--muted">
                          {this.publishDraft.swap_want_venue_id
                            ? '该场馆在所选日期下没有可用时段。'
                            : '请先选择「换得」的场馆。'}
                        </div>
                      ) : (
                        <div className="badminton-slot-checkboxes">
                          {this.swapWantSlotRows().map((s) => (
                            <label key={s.id} className="badminton-slot-checkbox-row">
                              <input
                                type="checkbox"
                                checked={(this.publishDraft.swap_want_slot_ids || []).includes(String(s.id))}
                                onchange={(e) => this.toggleSwapWantSlot(s.id, e.target.checked)}
                              />
                              <span className="badminton-slot-checkbox-text">
                                {s.name || `${s.start_time}-${s.end_time}`}{' '}
                                <span className="badminton-slot-checkbox-time">
                                  ({s.start_time}–{s.end_time})
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="badminton-form-field badminton-form-field--full">
                      {(this.publishDraft.swap_want_slot_ids || []).length > 0 ? (
                        <>
                          {publishFieldLabel(
                            false,
                            '换得 · 各时段场地',
                            '与出售相同；换入时段不要求在列表中相邻。'
                          )}
                          <div
                            className="badminton-form-grid badminton-form-grid--publish"
                            style="margin-top: 8px;"
                          >
                            {(this.publishDraft.swap_want_slot_ids || []).map((sidRaw) => {
                              const sid = String(sidRaw);
                              const row = this.swapWantSlotRows().find((s) => String(s.id) === sid);
                              const slotLabel =
                                row?.name || (row ? `${row.start_time}–${row.end_time}` : `#${sid}`);
                              return (
                                <label key={`swap-want-court-${sid}`} className="badminton-form-field">
                                  <span
                                    className="badminton-field-help"
                                    style="display:block;margin-bottom:4px;"
                                  >
                                    {slotLabel}
                                  </span>
                                  {this.renderSwapSlotCourtSelect(
                                    'swap_want_slot_courts',
                                    sid,
                                    this.publishDraft.swap_want_venue_id
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="badminton-form-grid badminton-form-grid--publish">
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '日期', '')}
                      <select
                        className="FormControl"
                        value={this.publishDraft.trade_date || this.selectedDate}
                        onchange={(e) => {
                          this.publishDraft.trade_date = e.target.value;
                          m.redraw();
                          void this.refreshSellPriceCap();
                        }}
                      >
                        {this.dateList.map((d) => (
                          <option key={d.date} value={d.date}>
                            {d.dayName} {d.dateStr}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '微信号', '仅意向双方可见，请填写真实可添加的微信号')}
                      <input
                        className="FormControl"
                        type="text"
                        autocomplete="off"
                        placeholder="例如：wxid_…"
                        value={this.publishDraft.wechat_plain}
                        oninput={(e) => {
                          this.publishDraft.wechat_plain = e.target.value;
                          m.redraw();
                        }}
                      />
                    </label>
                    <label className="badminton-form-field">
                      {publishFieldLabel(true, '场馆', '')}
                      <select
                        className={`FormControl${this.assistantFilled.venue_id ? ' badminton-assistant-filled' : ''}`}
                        value={this.publishDraft.venue_id}
                        onchange={(e) => {
                          this.publishDraft.venue_id = e.target.value;
                          this.publishDraft.court_id = '';
                          this.publishDraft.court_edge_pref = '';
                          this.publishDraft.slot_id = '';
                          this.publishDraft.slot_ids = [];
                          m.redraw();
                          void this.refreshSellPriceCap();
                        }}
                      >
                        <option value="">请选择场馆</option>
                        {venues.map((vn) => (
                          <option key={vn.id} value={vn.id}>
                            {vn.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="badminton-form-field badminton-form-field--full">
                      {publishFieldLabel(
                        true,
                        this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy' ? '时段（可多选）' : '时段',
                        this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy'
                          ? '与上方日期、场馆对应；至少勾选一个'
                          : this.publishDraft.trade_type === 'group'
                            ? '与上方日期、场馆对应；选完后再填是否已订场、缺员与级别'
                            : '请选择一个时段'
                      )}
                      {publishSlots.length === 0 ? (
                        <div className="badminton-field-help badminton-field-help--muted">
                          {this.publishDraft.venue_id
                            ? '该场馆在所选日期下没有可用时段（可能已全部结束，或后台尚未配置）。'
                            : '请先选择场馆。'}
                        </div>
                      ) : this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy' ? (
                        <div className="badminton-slot-checkboxes">
                          {publishSlots.map((s) => (
                            <label key={s.id} className="badminton-slot-checkbox-row">
                              <input
                                type="checkbox"
                                checked={this.publishDraft.slot_ids.includes(String(s.id))}
                                onchange={(e) => {
                                  const sid = String(s.id);
                                  if (e.target.checked) {
                                    if (!this.publishDraft.slot_ids.includes(sid)) this.publishDraft.slot_ids.push(sid);
                                  } else {
                                    this.publishDraft.slot_ids = this.publishDraft.slot_ids.filter((x) => x !== sid);
                                  }
                                  this.publishDraft.slot_id = this.publishDraft.slot_ids[0] || '';
                                  m.redraw();
                                  void this.refreshSellPriceCap();
                                }}
                              />
                              <span className="badminton-slot-checkbox-text">
                                {s.name || `${s.start_time}-${s.end_time}`}{' '}
                                <span className="badminton-slot-checkbox-time">
                                  ({s.start_time}–{s.end_time})
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <select
                          className="FormControl"
                          value={this.publishDraft.slot_id}
                          onchange={(e) => {
                            this.publishDraft.slot_id = e.target.value;
                            m.redraw();
                            void this.refreshSellPriceCap();
                          }}
                        >
                          <option value="">请选择时段</option>
                          {publishSlots.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name} ({s.start_time}-{s.end_time})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {this.publishDraft.trade_type === 'group' ? (
                      <>
                        <div className="badminton-form-field badminton-form-field--full">
                          {publishFieldLabel(true, '是否已拥有场地', '选「是」后可在下方「选填」中填写具体场地，或选择对他人展示为边场/非边场')}
                          <div className="badminton-checkbox-row" style="gap:16px;flex-wrap:wrap;">
                            <label className="badminton-checkbox-label">
                              <input
                                type="radio"
                                name="badminton-group-has-court"
                                checked={!this.publishDraft.group_has_court}
                                onchange={() => this.toggleGroupHasCourt(false)}
                              />
                              <span>否（尚未订场 / 到场再定）</span>
                            </label>
                            <label className="badminton-checkbox-label">
                              <input
                                type="radio"
                                name="badminton-group-has-court"
                                checked={!!this.publishDraft.group_has_court}
                                onchange={() => this.toggleGroupHasCourt(true)}
                              />
                              <span>是（已订场，可填场地编号）</span>
                            </label>
                          </div>
                        </div>
                        <label className="badminton-form-field">
                          {publishFieldLabel(true, '缺男生人数', '0～99；与女生人数之和须 ≥ 1')}
                          <input
                            className="FormControl"
                            type="number"
                            min="0"
                            max="99"
                            step="1"
                            value={this.publishDraft.group_missing_male}
                            oninput={(e) => {
                              this.publishDraft.group_missing_male = e.target.value;
                              m.redraw();
                            }}
                          />
                        </label>
                        <label className="badminton-form-field">
                          {publishFieldLabel(true, '缺女生人数', '0～99')}
                          <input
                            className="FormControl"
                            type="number"
                            min="0"
                            max="99"
                            step="1"
                            value={this.publishDraft.group_missing_female}
                            oninput={(e) => {
                              this.publishDraft.group_missing_female = e.target.value;
                              m.redraw();
                            }}
                          />
                        </label>
                        <div className="badminton-form-field badminton-form-field--full">
                          {publishFieldLabel(true, '希望匹配的中羽级别', '可多选，1～9（步长 0.5）')}
                          <div
                            className="badminton-slot-checkboxes"
                            style="display:flex;flex-wrap:wrap;gap:6px 14px;align-items:flex-start;"
                          >
                            {GROUP_SKILL_STEP_VALUES.map((step) => (
                              <label key={step} className="badminton-slot-checkbox-row" style="margin:0;">
                                <input
                                  type="checkbox"
                                  checked={(this.publishDraft.group_skill_levels || []).includes(String(step))}
                                  onchange={(e) => this.toggleGroupSkillLevel(step, e.target.checked)}
                                />
                                <span className="badminton-slot-checkbox-text" style="white-space:nowrap;">
                                  {Math.abs(step % 1) < 1e-6 ? String(Math.round(step)) : String(step)} 级
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <label className="badminton-form-field badminton-form-field--full">
                          {publishFieldLabel(true, '组局类型', '')}
                          <select
                            className="FormControl"
                            value={this.publishDraft.group_play_type}
                            onchange={(e) => {
                              this.publishDraft.group_play_type = e.target.value;
                              m.redraw();
                            }}
                          >
                            {GROUP_PLAY_TYPES.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </>
                    ) : null}

                    {this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy' ? (
                      <div className="badminton-form-field badminton-form-field--full">
                        <div className="badminton-checkbox-row">
                          <label className="badminton-checkbox-label">
                            <input
                              type="checkbox"
                              checked={!!this.publishDraft.allow_split}
                              onchange={(e) => {
                                this.publishDraft.allow_split = !!e.target.checked;
                                m.redraw();
                              }}
                            />
                            <span>允许分开{this.publishDraft.trade_type === 'sell' ? '出售' : '求购'}</span>
                          </label>
                        </div>
                        <p className="badminton-field-help">选填行为：勾选后，对方有可能只对接你选中的部分时段（以实际约定为准）。默认勾选。</p>
                      </div>
                    ) : null}

                    {this.publishDraft.trade_type === 'sell' || this.publishDraft.trade_type === 'buy' ? (
                      <div className="badminton-form-field badminton-form-field--full">
                        {publishFieldLabel(
                          true,
                          '价格',
                          this.publishDraft.trade_type === 'sell'
                            ? '合计出价；不得超过下方标准价合计上限，仅可填更低'
                            : '合计预算；不得超过下方标准价合计上限（与出售共用上限），仅可填更低'
                        )}
                        <div className="badminton-sell-price-wrap">
                          <input
                            className={`FormControl${this.assistantFilled.price ? ' badminton-assistant-filled' : ''}`}
                            type="number"
                            step="0.01"
                            min="0"
                            max={this.sellPriceCap && Number.isFinite(this.sellPriceCap.max_price) ? this.sellPriceCap.max_price : undefined}
                            placeholder={
                              this.sellPriceCap && Number.isFinite(this.sellPriceCap.max_price)
                                ? `0 ~ ¥${this.sellPriceCap.max_price}`
                                : '先选时段后显示上限'
                            }
                            value={this.publishDraft.price}
                            oninput={(e) => {
                              let v = e.target.value;
                              if (this.sellPriceCap && Number.isFinite(this.sellPriceCap.max_price) && v !== '') {
                                const n = parseFloat(v);
                                if (Number.isFinite(n) && n > this.sellPriceCap.max_price) {
                                  v = formatPriceAtMost(this.sellPriceCap.max_price);
                                }
                              }
                              this.publishDraft.price = v;
                              m.redraw();
                            }}
                          />
                          <div className="badminton-sell-cap-hint">
                            {this.sellPriceCapLoading ? (
                              <span className="badminton-field-help">正在计算标准价上限…</span>
                            ) : this.sellPriceCap && Number.isFinite(this.sellPriceCap.max_price) ? (
                              <div className="badminton-field-help">
                                <p style="margin: 0 0 8px;">
                                  当前已选时段标准价<strong className="badminton-sell-cap-num"> 合计上限 ¥{this.sellPriceCap.max_price}</strong>
                                  （身份：{TIER_LABELS[this.sellPriceCap.tier] || this.sellPriceCap.tier}）。
                                  {this.publishDraft.trade_type === 'sell'
                                    ? '售价仅可等于或低于该上限。'
                                    : '求购预算仅可等于或低于该上限。'}
                                </p>
                                <p style="margin: 0 0 8px; font-size: 12px; opacity: 0.92;">
                                  {CALENDAR_SUMMARY_LABELS[this.sellPriceCap.calendar_summary] || CALENDAR_SUMMARY_LABELS.weekday_slot}
                                </p>
                                {this.sellPriceCap.student_base && Object.keys(this.sellPriceCap.student_base).length ? (
                                  <p style="margin: 0 0 8px; font-size: 12px;">
                                    学生基础价（A/B/C，元/场段）：A ¥{this.sellPriceCap.student_base.A ?? '—'}、B ¥
                                    {this.sellPriceCap.student_base.B ?? '—'}、C ¥{this.sellPriceCap.student_base.C ?? '—'}
                                    <br />
                                    身份倍数：教职工 ×{this.sellPriceCap.staff_multiplier}，校友 ×{this.sellPriceCap.alumni_multiplier}（在对应档学生基础价上相乘）。
                                  </p>
                                ) : null}
                                {this.sellPriceCap.lines && this.sellPriceCap.lines.length ? (
                                  <ul style="margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.5;">
                                    {this.sellPriceCap.lines.map((ln) => (
                                      <li key={ln.slot_id}>
                                        {ln.slot_name || `时段 #${ln.slot_id}`}：配置档位 {ln.slot_row_code} → 本单计价档位{' '}
                                        {ln.billing_code}，学生基准 ¥{ln.base_student}
                                        {ln.uses_slot_base_price ? '（后台时段基础价）' : ''}，当前身份 ¥{ln.your_price}
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                            ) : (
                              <span className="badminton-field-help">请先选择日期并勾选至少一个时段，以显示标准价上限。</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="badminton-form-section">
                <div className="badminton-form-section-title">选填</div>
                <div className="badminton-form-grid badminton-form-grid--publish">
                  {this.publishDraft.trade_type === 'swap' ? null : this.publishDraft.trade_type ===
                    'group' && !this.publishDraft.group_has_court ? null : (
                    <label className="badminton-form-field">
                      {publishFieldLabel(
                        false,
                        '场地编号',
                        this.publishVenueAllowsEdge()
                          ? '不指定表示不限。选边场/非边场时，他人查看只显示该文字、不显示具体编号；与点选具体场地二选一'
                          : '不选表示不指定具体场地'
                      )}
                      <select
                        className="FormControl"
                        value={this.courtSelectValue()}
                        onchange={(e) => this.onCourtSelectChange(e)}
                      >
                      <option value="">不指定</option>
                      {this.publishVenueAllowsEdge() ? (
                        <optgroup label="对他人展示为（不计入具体场地号）">
                          <option value="__edge__">
                            {edgeCourtOptionLabelForPublish(this.config, this.publishDraft.venue_id)}
                          </option>
                          <option value="__non_edge__">非边场</option>
                        </optgroup>
                      ) : null}
                      {this.courtsForVenue(this.publishDraft.venue_id).length ? (
                        <optgroup label="具体场地">
                          {this.courtsForVenue(this.publishDraft.venue_id).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                      </select>
                    </label>
                  )}
                  <label className="badminton-form-field">
                    {publishFieldLabel(false, '最大意向数', '填 0 表示不限制可查看人数')}
                    <input
                      className="FormControl"
                      type="number"
                      min="0"
                      value={this.publishDraft.max_intents}
                      oninput={(e) => {
                        this.publishDraft.max_intents = e.target.value;
                        m.redraw();
                      }}
                    />
                  </label>
                  <label className="badminton-form-field badminton-form-field--full">
                    {publishFieldLabel(false, '备注', '')}
                    <textarea
                      className={`FormControl${this.assistantFilled.content ? ' badminton-assistant-filled' : ''}`}
                      rows="2"
                      placeholder="其他说明（选填）"
                      value={this.publishDraft.content}
                      oninput={(e) => {
                        this.publishDraft.content = e.target.value;
                        m.redraw();
                      }}
                    />
                  </label>
                  {includeSubmitButton ? (
                    <div className="Form-group badminton-publish-modal-submit">
                      <Button className="Button Button--primary Button--block" type="submit">
                        提交发布
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
      </div>
    );
  }

  view() {
    if (this.loading) {
      return (
        <div className="badminton-page-body">
          <div className="badminton-loading">加载中…</div>
        </div>
      );
    }

    if (this.error) {
      return (
        <div className="badminton-page-body">
          <p>{this.error}</p>
          <Button className="Button" onclick={() => this.goBack()}>
            返回
          </Button>
        </div>
      );
    }

    if (!this.capabilities?.viewTrade) {
      return (
        <div className="badminton-page-body">
          <p>你没有场地交易权限。</p>
          <Button className="Button" onclick={() => this.goBack()}>
            返回
          </Button>
        </div>
      );
    }

    const venues = this.enabledVenues();
    const vCur = this.currentVenue();
    const hint = this.dateList[0] ? `${this.dateList[0].dayName}（${this.dateList[0].dateStr}）` : '';

    return (
      <div className="badminton-page-body">
        <div className="badminton-board">
          {this.toastOk ? <div className="badminton-alert ok">{this.toastOk}</div> : null}
          {this.toastErr ? <div className="badminton-alert err">{this.toastErr}</div> : null}
          <BadmintonAssistantFloat board={this} />

          <div className="badminton-venue-tabs">
            {venues.map((vn) => (
              <button
                key={vn.id}
                type="button"
                className={`badminton-pill ${Number(this.selectedVenueId) === Number(vn.id) ? 'active' : ''}`}
                onclick={() => this.selectVenue(Number(vn.id))}
              >
                {vn.name}
              </button>
            ))}
          </div>

          {vCur?.open_time ? (
            <div className="badminton-open-time">
              <strong>开放时间</strong> {vCur.open_time}
            </div>
          ) : null}

          <div className="badminton-date-box">
            <div className="badminton-date-title">
              选择日期
              {hint ? <span>{hint}</span> : null}
            </div>
            <div className="badminton-date-strip">
              {this.dateList.map((d, i) => (
                <div
                  key={d.date}
                  className={`badminton-date-item ${d.isActive ? 'active' : ''}`}
                  onclick={() => this.selectDate(i)}
                >
                  <div>{d.dayName}</div>
                  <div>{d.dateStr}</div>
                </div>
              ))}
            </div>
          </div>


          <div className="badminton-actions">
            <span>
              <strong style="margin-right: 6px;">快速发布:</strong>
              <Button className="Button" onclick={() => this.ensureLoginOrOpenPublish('sell')} style="margin-right: 4px;">
                出售
              </Button>
              <Button className="Button" onclick={() => this.ensureLoginOrOpenPublish('buy')} style="margin-right: 4px;">
                求购
              </Button>
              <Button className="Button" onclick={() => this.ensureLoginOrOpenPublish('swap')} style="margin-right: 4px;">
                交换
              </Button>
              <Button className="Button" onclick={() => this.ensureLoginOrOpenPublish('group')}>
                组局
              </Button>
            </span>
          </div>

          <div className="badminton-matrix-wrap">
            {this.slotRows.length === 0 ? (
              <div style="padding: 16px; color: var(--muted-color);">后台尚未配置时段，请在管理后台维护「场次时段」。</div>
            ) : (
              <table className="badminton-matrix">
                <thead>
                  <tr>
                    <th className="sticky-col">
                      <div className="badminton-time-head-title">时间</div>
                      {this.capabilities.viewTrade ? (
                        <div className="badminton-time-head-sub">（点击时间订阅）</div>
                      ) : null}
                    </th>
                    <th
                      className="badminton-h-sell"
                      onclick={() => this.ensureLoginOrOpenPublish('sell')}
                    >
                      出售
                    </th>
                    <th
                      className="badminton-h-buy"
                      onclick={() => this.ensureLoginOrOpenPublish('buy')}
                    >
                      求购
                    </th>
                    <th
                      className="badminton-h-swap"
                      onclick={() => this.ensureLoginOrOpenPublish('swap')}
                    >
                      交换
                    </th>
                    <th
                      className="badminton-h-group"
                      onclick={() => this.ensureLoginOrOpenPublish('group')}
                    >
                      组局
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {this.slotRows.map((slot, row) => (
                    <tr key={slot.id}>
                      <td
                        className={`sticky-col${
                          this.capabilities.viewTrade ? ' badminton-time-slot-cell--subscribe' : ''
                        }`}
                        onclick={(e) => {
                          if (!this.capabilities.viewTrade) return;
                          e.preventDefault();
                          e.stopPropagation();
                          this.openSubscribeSlotModal(slot);
                        }}
                        title={this.capabilities.viewTrade ? '点击订阅该时段' : undefined}
                      >
                        <div className="badminton-time-cell-title">
                          {slot.name || `${slot.start_time}-${slot.end_time}`}
                        </div>
                      </td>
                      {[0, 1, 2, 3].map((col) => (
                        <td
                          key={col}
                          className={`badminton-count-cell badminton-count-cell--lvl-${Math.max(
                            0,
                            Math.min(9, (this.counts[row] && this.counts[row][col]) || 0)
                          )} ${this.selectedRow === row && this.selectedCol === col ? 'selected' : ''}`}
                          onclick={() => this.onCellClick(row, col)}
                        >
                          {(this.counts[row] && this.counts[row][col]) || 0} 条
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {this.detailTrades.length > 0 || (this.selectedRow >= 0 && this.selectedCol >= 0) ? (
            <div className="badminton-sheet">
              <h4>{this.detailTitle || '交易明细'}</h4>
              {this.detailTrades.length === 0 ? (
                <div style="color: var(--muted-color);">暂无记录</div>
              ) : (
                this.detailTrades.map((t) => (
                  <div
                    key={t.id}
                    className="badminton-trade-row badminton-trade-row--clickable"
                    onclick={() => this.handleTradeRowClick(t)}
                  >
                    <div>
                      <div style="font-weight: 600;">
                        {TYPE_LABELS[t.trade_type] || t.trade_type} · {t.venue_name || '未指定场馆'}
                      </div>
                      <div style="font-size: 12px; color: var(--muted-color);">
                        {tradeViewCountLine(t)} ·{' '}
                        {t.remaining_intents != null ? `剩余可查看 ${t.remaining_intents} 次 · ` : '不限次数 · '}
                        {t.payload?.merged_slots_text ? `${t.payload.merged_slots_text} · ` : ''}
                        {t.slot_name && !t.payload?.merged_slots_text ? `${t.slot_name} · ` : ''}
                        {t.trade_type === 'swap' || t.court_name || t.payload?.court_edge_pref
                          ? `${courtDisplayForTrade(t, this.config)} · `
                          : ''}
                        {t.price != null && Number(t.price) > 0 ? `¥${t.price}` : ''}
                      </div>
                      {canViewBadmintonTradeRemark() && t.content ? (
                        <div
                          style="
                            font-size: 11px;
                            color: var(--muted-color);
                            margin-top: 4px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            max-width: 100%;
                          "
                        >
                          备注：{String(t.content).length > 24 ? `${String(t.content).slice(0, 24)}…` : String(t.content)}
                        </div>
                      ) : null}
                      <div style="font-size: 11px; color: var(--muted-color); margin-top: 4px;">点击查看详情与微信号</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}

          <div className="badminton-sheet">
            <h4>我发布的信息</h4>
            {this.myTrades.length === 0 ? (
              <div style="color: var(--muted-color);">暂无记录。</div>
            ) : (
              this.myTrades.map((t) => (
                <div key={`my-${t.id}`} className="badminton-trade-row badminton-trade-row--my-published">
                  <div className="badminton-trade-row__main">
                    <div style="font-weight: 600;">[{TYPE_LABELS[t.trade_type] || t.trade_type}]</div>
                    <div className="badminton-trade-row__meta">
                      {tradeViewCountLine(t)} · {t.trade_date} · {t.venue_name || '未指定场馆'} ·{' '}
                      {t.payload?.merged_slots_text || t.slot_name || '未指定时段'} ·{' '}
                      {courtDisplayForTrade(t, this.config)} · 状态:{' '}
                      {tradeStatusLabel(t.status)}
                      {t.status === 'open' || t.status === 'matched' ? (
                        <>
                          {' · '}
                          <button
                            type="button"
                            className="badminton-withdraw-link"
                            onclick={(e) => {
                              e.stopPropagation();
                              this.withdrawMyTrade(t);
                            }}
                          >
                            撤下
                          </button>
                        </>
                      ) : null}
                    </div>
                        {t.content ? (
                          <div className="badminton-trade-row__remark">
                            备注：{String(t.content).length > 24 ? `${String(t.content).slice(0, 24)}…` : String(t.content)}
                          </div>
                        ) : null}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="badminton-sheet">
            <h4>我响应的信息</h4>
            {this.myIntentTrades.length === 0 ? (
              <div style="color: var(--muted-color);">暂无记录。</div>
            ) : (
              this.myIntentTrades.map((t) => (
                <div
                  key={`intent-${t.intent_id}`}
                  className="badminton-trade-row badminton-trade-row--clickable"
                  onclick={() =>
                    this.showTradeDetailModal(t, t.wechat_plain || t.wechat_masked || '')
                  }
                >
                  <div>
                    <div style="font-weight: 600;">[{TYPE_LABELS[t.trade_type] || t.trade_type}]</div>
                    <div style="font-size: 12px; color: var(--muted-color);">
                      {tradeViewCountLine(t)} · {t.trade_date} · {t.venue_name || '未指定场馆'} ·{' '}
                      {t.payload?.merged_slots_text || t.slot_name || '未指定时段'} · 状态: {tradeStatusLabel(t.status)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="badminton-sheet">
            <h4>我的订阅</h4>
            {this.mySubscriptions.length === 0 ? (
              <div style="color: var(--muted-color);">
                暂无订阅。点击矩阵左侧对应时段格即可添加；表头「时间」下列有说明。最多同时 2 条进行中，过期或场次结束后将自动结束。
              </div>
            ) : (
              this.mySubscriptions.map((s) => (
                <div key={`sub-${s.id}`} className="badminton-trade-row">
                  <div>
                    <div style="font-weight: 600;">
                      [订阅 {TYPE_LABELS[s.trade_type] || s.trade_type}]
                    </div>
                    <div style="font-size: 12px; color: var(--muted-color);">
                      {s.trade_date} · {s.venue_name || '—'} ·{' '}
                      {s.slot_name ||
                        (s.slot_start_time && s.slot_end_time
                          ? `${s.slot_start_time}-${s.slot_end_time}`
                          : '—')}{' '}
                      · {subscriptionCourtLabel(s, this.config)} · 状态: {subscriptionStatusLabel(s.status)}
                    </div>
                  </div>
                  {s.status === 'open' ? (
                    <Button className="Button" onclick={() => this.withdrawMySubscription(s)}>
                      取消订阅
                    </Button>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }
}
